// ============================================================================
// AUTO-GENERATED — DO NOT EDIT BY HAND.
// Source of truth: backend/analysis/distributionEngine.js
// Regenerate:      npm run gen:engine   (runs automatically on prebuild/prestart)
//
// The frontend copy exists because CRA ModuleScopePlugin blocks importing the
// backend file directly. The executable code is a 1:1 transform of the backend
// source — only the export surface differs. Guarded by engineGenerated.staleness
// and engineParity tests. See ADR-008.
// ============================================================================

/**
 * Distribution Engine - Phase 3
 *
 * Provides MLE fitting, Anderson-Darling GOF testing, AIC/BIC calculations,
 * and Likelihood Ratio Tests for nested model comparisons.
 *
 * Supported distributions:
 * - Normal (2-parameter)
 * - Lognormal (2-parameter) and Lognormal 3P (3-parameter with threshold)
 * - Weibull (2-parameter) and Weibull 3P (3-parameter with threshold)
 * - Gamma (2-parameter) and Gamma 3P (3-parameter with threshold)
 * - Exponential (1-parameter)
 */

// ============================================================================
// Mathematical Helper Functions
// ============================================================================

/**
 * Standard normal CDF (Phi function) using Abramowitz & Stegun approximation
 */
export function normalCDF(x) {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x) / Math.sqrt(2);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return 0.5 * (1.0 + sign * y);
}

/**
 * Natural logarithm of the gamma function using Lanczos approximation
 */
function lnGamma(z) {
    if (z < 0.5) {
        return Math.log(Math.PI / Math.sin(Math.PI * z)) - lnGamma(1 - z);
    }
    z -= 1;
    const g = 7;
    const c = [
        0.99999999999980993,
        676.5203681218851,
        -1259.1392167224028,
        771.32342877765313,
        -176.61502916214059,
        12.507343278686905,
        -0.13857109526572012,
        9.9843695780195716e-6,
        1.5056327351493116e-7
    ];

    let x = c[0];
    for (let i = 1; i < g + 2; i++) {
        x += c[i] / (z + i);
    }

    const t = z + g + 0.5;
    return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

/**
 * Gamma function
 */
function gamma(z) {
    return Math.exp(lnGamma(z));
}

/**
 * Lower incomplete gamma function P(a,x) = γ(a,x)/Γ(a)
 * Using series expansion for small x and continued fraction for large x
 */
function gammaCDF(x, shape, rate) {
    if (x <= 0) return 0;
    const a = shape;
    const scaledX = x * rate;

    if (scaledX < a + 1) {
        // Series expansion for lower incomplete gamma P(a, x)
        let sum = 1.0 / a;
        let term = sum;
        for (let n = 1; n < 100; n++) {
            term *= scaledX / (a + n);
            sum += term;
            if (Math.abs(term) < 1e-10 * Math.abs(sum)) break;
        }
        return sum * Math.exp(-scaledX + a * Math.log(scaledX) - lnGamma(a));
    } else {
        // Modified Lentz continued fraction for upper incomplete gamma Q(a, x)
        // Reference: Numerical Recipes in C, 2nd ed., section 6.2 (gcf routine).
        // Do NOT hand-roll this CF: a prior frontend copy diverged from Minitab as
        // shape grew (wrong Lentz init + b-recurrence off by 2). See FIX-GAMMACDF /
        // docs/investigations/frontend-gammacdf-ad-divergence.md.
        const FPMIN = 1e-30;
        let b = scaledX + 1.0 - a;          // b_0
        let c = 1.0 / FPMIN;                // c_0 = 1/FPMIN (large, per Lentz method)
        let d = 1.0 / (Math.abs(b) < FPMIN ? FPMIN : b);  // d_0 = 1/b_0
        let h = d;
        for (let i = 1; i < 200; i++) {
            const an = -i * (i - a);        // a_i = -i*(i-a)
            b += 2.0;                        // b_i = b_0 + 2i
            d = an * d + b;
            if (Math.abs(d) < FPMIN) d = FPMIN;
            c = b + an / c;
            if (Math.abs(c) < FPMIN) c = FPMIN;
            d = 1.0 / d;
            const del = d * c;
            h *= del;
            if (Math.abs(del - 1.0) < 1e-10) break;
        }
        return 1.0 - Math.exp(-scaledX + a * Math.log(scaledX) - lnGamma(a)) * h;
    }
}

/**
 * Weibull CDF
 */
function weibullCDF(x, shape, scale) {
    if (x <= 0) return 0;
    return 1 - Math.exp(-Math.pow(x / scale, shape));
}

/**
 * Exponential CDF
 */
function exponentialCDF(x, rate) {
    if (x <= 0) return 0;
    return 1 - Math.exp(-rate * x);
}

/**
 * Lognormal CDF
 */
function lognormalCDF(x, mu, sigma) {
    if (x <= 0) return 0;
    return normalCDF((Math.log(x) - mu) / sigma);
}

/**
 * Johnson SU CDF
 * Transformation: z = gamma + delta * asinh((x - xi) / lambda)
 * @param {number} x - Value
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} xi - Location parameter
 * @param {number} lambda - Scale parameter, lambda > 0
 */
function johnsonSUCDF(x, gamma, delta, xi, lambda) {
    const z = gamma + delta * Math.asinh((x - xi) / lambda);
    return normalCDF(z);
}

/**
 * Johnson SB CDF (bounded)
 * Transformation: z = gamma + delta * ln((x - L) / (U - x))
 * @param {number} x - Value (must be in (L, U))
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} L - Lower bound
 * @param {number} U - Upper bound
 */
function johnsonSBCDF(x, gamma, delta, L, U) {
    if (x <= L) return 0;
    if (x >= U) return 1;
    const y = (x - L) / (U - x);
    const z = gamma + delta * Math.log(y);
    return normalCDF(z);
}

/**
 * Chi-square CDF for LRT p-value computation
 * Chi-square(df) is Gamma(df/2, 1/2)
 */
function chiSquareCDF(x, df) {
    if (x <= 0) return 0;
    return gammaCDF(x, df / 2, 0.5);
}

// ============================================================================
// Probability Density Functions (PDF)
// ============================================================================

/**
 * Standard normal PDF
 */
function normalPDF(x, mu, sigma) {
    const z = (x - mu) / sigma;
    return (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
}

/**
 * Lognormal PDF
 */
function lognormalPDF(x, mu, sigma) {
    if (x <= 0) return 0;
    const logX = Math.log(x);
    const z = (logX - mu) / sigma;
    return (1 / (x * sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
}

/**
 * Weibull PDF
 */
function weibullPDF(x, shape, scale) {
    if (x <= 0) return 0;
    const xNorm = x / scale;
    return (shape / scale) * Math.pow(xNorm, shape - 1) * Math.exp(-Math.pow(xNorm, shape));
}

/**
 * Gamma PDF
 */
function gammaPDF(x, shape, rate) {
    if (x <= 0) return 0;
    return (Math.pow(rate, shape) / gamma(shape)) * Math.pow(x, shape - 1) * Math.exp(-rate * x);
}

/**
 * Exponential PDF
 */
function exponentialPDF(x, rate) {
    if (x <= 0) return 0;
    return rate * Math.exp(-rate * x);
}

/**
 * Johnson SU PDF
 * @param {number} x - Value
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} xi - Location parameter
 * @param {number} lambda - Scale parameter, lambda > 0
 */
function johnsonSUPDF(x, gamma, delta, xi, lambda) {
    const u = (x - xi) / lambda;
    const z = gamma + delta * Math.asinh(u);
    const sqrtTerm = Math.sqrt(u * u + 1);
    // PDF = (delta / (lambda * sqrt(2*pi) * sqrt(u^2 + 1))) * exp(-z^2 / 2)
    return (delta / (lambda * Math.sqrt(2 * Math.PI) * sqrtTerm)) * Math.exp(-0.5 * z * z);
}

/**
 * Johnson SB PDF (bounded)
 * @param {number} x - Value (must be in (L, U))
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} L - Lower bound
 * @param {number} U - Upper bound
 */
function johnsonSBPDF(x, gamma, delta, L, U) {
    if (x <= L || x >= U) return 0;
    const y = (x - L) / (U - x);
    const z = gamma + delta * Math.log(y);
    // Jacobian: d/dx[ln((x-L)/(U-x))] = (U-L) / ((x-L)(U-x))
    const jacobian = (U - L) / ((x - L) * (U - x));
    // PDF = delta * jacobian * phi(z) where phi is standard normal PDF
    return (delta * jacobian / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * z * z);
}

// ============================================================================
// Percent Point Functions (PPF / Inverse CDF / Quantile Functions)
// ============================================================================

/**
 * Standard normal PPF using Acklam's algorithm
 * High accuracy approximation for the inverse of the standard normal CDF
 */
function standardNormalPPF(p) {
    if (p <= 0) return -Infinity;
    if (p >= 1) return Infinity;
    if (p === 0.5) return 0;

    // Coefficients for rational approximation
    const a = [
        -3.969683028665376e+01,
         2.209460984245205e+02,
        -2.759285104469687e+02,
         1.383577518672690e+02,
        -3.066479806614716e+01,
         2.506628277459239e+00
    ];
    const b = [
        -5.447609879822406e+01,
         1.615858368580409e+02,
        -1.556989798598866e+02,
         6.680131188771972e+01,
        -1.328068155288572e+01
    ];
    const c = [
        -7.784894002430293e-03,
        -3.223964580411365e-01,
        -2.400758277161838e+00,
        -2.549732539343734e+00,
         4.374664141464968e+00,
         2.938163982698783e+00
    ];
    const d = [
         7.784695709041462e-03,
         3.224671290700398e-01,
         2.445134137142996e+00,
         3.754408661907416e+00
    ];

    const pLow = 0.02425;
    const pHigh = 1 - pLow;

    let q, r;

    if (p < pLow) {
        // Lower region
        q = Math.sqrt(-2 * Math.log(p));
        return (((((c[0]*q + c[1])*q + c[2])*q + c[3])*q + c[4])*q + c[5]) /
               ((((d[0]*q + d[1])*q + d[2])*q + d[3])*q + 1);
    } else if (p <= pHigh) {
        // Central region
        q = p - 0.5;
        r = q * q;
        return (((((a[0]*r + a[1])*r + a[2])*r + a[3])*r + a[4])*r + a[5]) * q /
               (((((b[0]*r + b[1])*r + b[2])*r + b[3])*r + b[4])*r + 1);
    } else {
        // Upper region
        q = Math.sqrt(-2 * Math.log(1 - p));
        return -(((((c[0]*q + c[1])*q + c[2])*q + c[3])*q + c[4])*q + c[5]) /
                ((((d[0]*q + d[1])*q + d[2])*q + d[3])*q + 1);
    }
}

/**
 * Normal PPF
 */
function normalPPF(p, mu, sigma) {
    return mu + sigma * standardNormalPPF(p);
}

/**
 * Lognormal PPF
 */
function lognormalPPF(p, mu, sigma) {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    return Math.exp(mu + sigma * standardNormalPPF(p));
}

/**
 * Weibull PPF
 */
function weibullPPF(p, shape, scale) {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    return scale * Math.pow(-Math.log(1 - p), 1 / shape);
}

/**
 * Gamma PPF using Newton-Raphson iteration
 * More complex since gamma CDF has no closed-form inverse
 */
function gammaPPF(p, shape, rate) {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;

    // Initial estimate using Wilson-Hilferty approximation
    const z = standardNormalPPF(p);
    let x;

    if (shape >= 1) {
        // Wilson-Hilferty transformation
        const h = 1 - 1 / (9 * shape);
        const v = z * Math.sqrt(1 / (9 * shape));
        x = shape * Math.pow(h + v, 3) / rate;
    } else {
        // For small shape, use simpler estimate
        x = Math.pow(p * gamma(shape + 1), 1 / shape) / rate;
    }

    // Newton-Raphson refinement
    for (let iter = 0; iter < 30; iter++) {
        const cdf = gammaCDF(x, shape, rate);
        const pdf = gammaPDF(x, shape, rate);

        if (pdf < 1e-10) break;

        const delta = (cdf - p) / pdf;
        x = Math.max(1e-10, x - delta);

        if (Math.abs(delta) < 1e-10 * x) break;
    }

    return x;
}

/**
 * Exponential PPF
 */
function exponentialPPF(p, rate) {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    return -Math.log(1 - p) / rate;
}

/**
 * Johnson SU PPF (inverse CDF)
 * x = xi + lambda * sinh((Phi^-1(p) - gamma) / delta)
 * @param {number} p - Probability
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} xi - Location parameter
 * @param {number} lambda - Scale parameter, lambda > 0
 */
function johnsonSUPPF(p, gamma, delta, xi, lambda) {
    if (p <= 0) return -Infinity;
    if (p >= 1) return Infinity;
    const z = standardNormalPPF(p);
    return xi + lambda * Math.sinh((z - gamma) / delta);
}

/**
 * Johnson SB PPF (inverse CDF, bounded)
 * Inverse of z = gamma + delta * ln((x - L) / (U - x))
 * x = L + (U - L) / (1 + exp(-(z - gamma) / delta))
 * @param {number} p - Probability
 * @param {number} gamma - Shape parameter (skewness)
 * @param {number} delta - Shape parameter (kurtosis), delta > 0
 * @param {number} L - Lower bound
 * @param {number} U - Upper bound
 */
function johnsonSBPPF(p, gamma, delta, L, U) {
    if (p <= 0) return L;
    if (p >= 1) return U;
    const z = standardNormalPPF(p);
    const w = (z - gamma) / delta;
    // x = L + (U - L) * exp(w) / (1 + exp(w)) = L + (U - L) / (1 + exp(-w))
    // To avoid overflow, use the logistic sigmoid formulation
    if (w > 0) {
        const expNegW = Math.exp(-w);
        return L + (U - L) / (1 + expNegW);
    } else {
        const expW = Math.exp(w);
        return L + (U - L) * expW / (1 + expW);
    }
}

/**
 * Compute interquartile range (IQR)
 */
function computeIQR(data) {
    const sorted = [...data].sort((a, b) => a - b);
    const n = sorted.length;
    const q1Index = Math.floor(n * 0.25);
    const q3Index = Math.floor(n * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    return q3 - q1;
}

// ============================================================================
// Maximum Likelihood Estimation
// ============================================================================

/**
 * Fit Normal distribution using MLE
 *
 * NOTE: MLE sigma uses n divisor (population variance), which differs from sample
 * standard deviation (n-1 divisor). This MLE sigma is correct for:
 * - Distribution fitting and ranking (AIC, BIC, log-likelihood)
 * - Comparing against other distributions
 *
 * For PPM calculations in the "Practical Impact" section, runAnalysis.js overrides
 * this CDF with one using sample sigma (n-1) to match the traditional "Overall PPM"
 * calculation. This ensures user-visible PPM values are consistent.
 */
function fitNormal(data) {
    const n = data.length;
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const variance = data.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;  // MLE: n divisor
    const sigma = Math.sqrt(variance);

    // Log-likelihood
    const logLik = -n / 2 * Math.log(2 * Math.PI) - n / 2 * Math.log(variance) - n / 2;

    return {
        params: { mu: mean, sigma: sigma },
        logLikelihood: logLik,
        numParams: 2,
        cdf: (x) => normalCDF((x - mean) / sigma),
        pdf: (x) => normalPDF(x, mean, sigma),
        ppf: (p) => normalPPF(p, mean, sigma)
    };
}

/**
 * Fit Lognormal distribution using MLE
 * Requires all positive data
 */
function fitLognormal(data) {
    // Check data validity
    if (data.some(x => x <= 0)) {
        return null; // Cannot fit lognormal to non-positive data
    }

    const logData = data.map(x => Math.log(x));
    const n = data.length;
    const mu = logData.reduce((a, b) => a + b, 0) / n;
    const variance = logData.reduce((sum, x) => sum + Math.pow(x - mu, 2), 0) / n;
    const sigma = Math.sqrt(variance);

    // Log-likelihood for lognormal
    const logLik = -n / 2 * Math.log(2 * Math.PI) - n / 2 * Math.log(variance)
                   - logData.reduce((sum, x) => sum + Math.pow(x - mu, 2), 0) / (2 * variance)
                   - logData.reduce((a, b) => a + b, 0); // Jacobian term

    return {
        params: { mu: mu, sigma: sigma },
        logLikelihood: logLik,
        numParams: 2,
        cdf: (x) => lognormalCDF(x, mu, sigma),
        pdf: (x) => lognormalPDF(x, mu, sigma),
        ppf: (p) => lognormalPPF(p, mu, sigma)
    };
}

/**
 * Fit Weibull distribution using MLE
 * Uses Newton-Raphson iteration
 */
function fitWeibull(data) {
    // Check data validity
    if (data.some(x => x <= 0)) {
        return null;
    }

    const n = data.length;
    const logData = data.map(x => Math.log(x));
    const sumLog = logData.reduce((a, b) => a + b, 0);

    // Initial estimate for shape using method of moments approximation
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const variance = data.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;
    const cv = Math.sqrt(variance) / mean;
    let shape = Math.pow(cv, -1.086); // Approximation

    // Newton-Raphson for shape parameter
    for (let iter = 0; iter < 50; iter++) {
        const sumXk = data.reduce((sum, x) => sum + Math.pow(x, shape), 0);
        const sumXkLogX = data.reduce((sum, x) => sum + Math.pow(x, shape) * Math.log(x), 0);
        const sumXkLogX2 = data.reduce((sum, x) => sum + Math.pow(x, shape) * Math.pow(Math.log(x), 2), 0);

        const f = sumXkLogX / sumXk - 1 / shape - sumLog / n;
        const fPrime = (sumXkLogX2 * sumXk - sumXkLogX * sumXkLogX) / (sumXk * sumXk) + 1 / (shape * shape);

        const delta = f / fPrime;
        shape -= delta;

        if (shape <= 0) shape = 0.1;
        if (Math.abs(delta) < 1e-8) break;
    }

    // Scale parameter
    const scale = Math.pow(data.reduce((sum, x) => sum + Math.pow(x, shape), 0) / n, 1 / shape);

    // Log-likelihood
    const logLik = n * Math.log(shape) - n * shape * Math.log(scale)
                   + (shape - 1) * sumLog
                   - data.reduce((sum, x) => sum + Math.pow(x / scale, shape), 0);

    return {
        params: { shape: shape, scale: scale },
        logLikelihood: logLik,
        numParams: 2,
        cdf: (x) => weibullCDF(x, shape, scale),
        pdf: (x) => weibullPDF(x, shape, scale),
        ppf: (p) => weibullPPF(p, shape, scale)
    };
}

/**
 * Fit Gamma distribution using MLE
 * Uses Newton-Raphson iteration
 */
function fitGamma(data) {
    // Check data validity
    if (data.some(x => x <= 0)) {
        return null;
    }

    const n = data.length;
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const sumLog = data.reduce((sum, x) => sum + Math.log(x), 0);
    const s = Math.log(mean) - sumLog / n;

    // Initial estimate using Minka's approximation
    let shape = (3 - s + Math.sqrt(Math.pow(s - 3, 2) + 24 * s)) / (12 * s);

    // Newton-Raphson for shape
    for (let iter = 0; iter < 50; iter++) {
        // Digamma function approximation
        const psi = Math.log(shape) - 1 / (2 * shape) - 1 / (12 * shape * shape);
        const psiPrime = 1 / shape + 1 / (2 * shape * shape) + 1 / (6 * Math.pow(shape, 3));

        const f = Math.log(shape) - psi - s;
        const fPrime = 1 / shape - psiPrime;

        const delta = f / fPrime;
        shape -= delta;

        if (shape <= 0) shape = 0.1;
        if (Math.abs(delta) < 1e-8) break;
    }

    const rate = shape / mean;

    // Log-likelihood
    const logLik = n * shape * Math.log(rate) - n * lnGamma(shape)
                   + (shape - 1) * sumLog - rate * data.reduce((a, b) => a + b, 0);

    return {
        params: { shape: shape, rate: rate },
        logLikelihood: logLik,
        numParams: 2,
        cdf: (x) => gammaCDF(x, shape, rate),
        pdf: (x) => gammaPDF(x, shape, rate),
        ppf: (p) => gammaPPF(p, shape, rate)
    };
}

/**
 * Fit Exponential distribution using MLE
 */
function fitExponential(data) {
    // Check data validity
    if (data.some(x => x <= 0)) {
        return null;
    }

    const n = data.length;
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const rate = 1 / mean;

    // Log-likelihood
    const logLik = n * Math.log(rate) - rate * data.reduce((a, b) => a + b, 0);

    return {
        params: { rate: rate },
        logLikelihood: logLik,
        numParams: 1,
        cdf: (x) => exponentialCDF(x, rate),
        pdf: (x) => exponentialPDF(x, rate),
        ppf: (p) => exponentialPPF(p, rate)
    };
}

/**
 * Fit Johnson SU distribution using MLE
 * Uses Slifker & Shapiro's quantile-based method for initial estimates,
 * then refines with numerical optimization.
 *
 * @param {number[]} data - Array of numeric values
 * @returns {Object|null} Fit result with params {gamma, delta, xi, lambda}
 */
function fitJohnsonSU(data) {
    const n = data.length;
    if (n < 10) {
        // Need sufficient data for 4-parameter estimation
        return null;
    }

    const sorted = [...data].sort((a, b) => a - b);

    // Compute sample moments
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const m2 = data.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;
    const m3 = data.reduce((sum, x) => sum + Math.pow(x - mean, 3), 0) / n;
    const m4 = data.reduce((sum, x) => sum + Math.pow(x - mean, 4), 0) / n;

    const stdDev = Math.sqrt(m2);
    const skewness = m3 / Math.pow(stdDev, 3);
    const kurtosis = m4 / Math.pow(stdDev, 4) - 3; // Excess kurtosis

    // Get quantiles for Slifker-Shapiro method
    const getQuantile = (p) => {
        const idx = Math.floor(p * (n - 1));
        const frac = p * (n - 1) - idx;
        if (idx >= n - 1) return sorted[n - 1];
        return sorted[idx] * (1 - frac) + sorted[idx + 1] * frac;
    };

    // Use z-values at specific quantiles for initial parameter estimation
    const z = 0.524; // Corresponds to ~70th percentile for normal
    const x_mz = getQuantile(normalCDF(-z));  // Lower quantile
    const x_pz = getQuantile(normalCDF(z));   // Upper quantile
    const x_m3z = getQuantile(normalCDF(-3 * z));
    const x_p3z = getQuantile(normalCDF(3 * z));

    // Slifker-Shapiro initial estimates
    const m = x_pz - x_mz;
    const p_val = x_p3z - x_pz;
    const q = x_mz - x_m3z;

    // Check for valid initial estimate conditions
    if (m <= 0 || p_val <= 0 || q <= 0 || p_val * q <= m * m) {
        // Fall back to moment-based initialization
        return fitJohnsonSUMoments(data, mean, stdDev, skewness, kurtosis);
    }

    // Initial parameter estimates using Slifker-Shapiro
    const eta = 2 * z;
    const pq = p_val * q;
    const m2_val = m * m;

    let delta_init, gamma_init, lambda_init, xi_init;

    try {
        delta_init = eta / Math.acosh(0.5 * Math.sqrt((pq / m2_val + 1) * ((p_val + q) / m + 2)));
        if (!isFinite(delta_init) || delta_init <= 0) delta_init = 1.0;

        const ratio = (p_val - q) / (2 * m);
        gamma_init = delta_init * Math.asinh(ratio / Math.sqrt((pq / m2_val) - 1));
        if (!isFinite(gamma_init)) gamma_init = 0;

        lambda_init = m * Math.sqrt(pq / m2_val - 1) /
                      (2 * Math.sinh(eta / delta_init));
        if (!isFinite(lambda_init) || lambda_init <= 0) lambda_init = stdDev;

        xi_init = (x_pz + x_mz) / 2 - lambda_init * Math.sinh(gamma_init / delta_init);
        if (!isFinite(xi_init)) xi_init = mean;
    } catch (e) {
        // Fallback to simpler initialization
        delta_init = 1.0;
        gamma_init = -skewness * 0.5;
        lambda_init = stdDev;
        xi_init = mean;
    }

    // Refine estimates using numerical optimization (coordinate descent)
    let gamma = gamma_init;
    let delta = Math.max(0.1, delta_init);
    let xi = xi_init;
    let lambda = Math.max(0.01, lambda_init);

    // Log-likelihood function
    const logLik = (g, d, x, l) => {
        if (d <= 0 || l <= 0) return -Infinity;
        let ll = 0;
        for (let i = 0; i < n; i++) {
            const u = (data[i] - x) / l;
            const z = g + d * Math.asinh(u);
            const sqrtTerm = Math.sqrt(u * u + 1);
            if (sqrtTerm <= 0) return -Infinity;
            ll += Math.log(d) - Math.log(l) - 0.5 * Math.log(2 * Math.PI)
                  - Math.log(sqrtTerm) - 0.5 * z * z;
        }
        return ll;
    };

    let bestLL = logLik(gamma, delta, xi, lambda);
    let converged = false;

    // Coordinate descent optimization
    for (let iter = 0; iter < 100; iter++) {
        const prevLL = bestLL;

        // Optimize each parameter in turn
        // Optimize gamma
        for (const tryGamma of [gamma - 0.1, gamma + 0.1, gamma - 0.01, gamma + 0.01]) {
            const ll = logLik(tryGamma, delta, xi, lambda);
            if (ll > bestLL) {
                gamma = tryGamma;
                bestLL = ll;
            }
        }

        // Optimize delta (must be positive)
        for (const tryDelta of [delta * 0.9, delta * 1.1, delta * 0.99, delta * 1.01]) {
            if (tryDelta > 0.01) {
                const ll = logLik(gamma, tryDelta, xi, lambda);
                if (ll > bestLL) {
                    delta = tryDelta;
                    bestLL = ll;
                }
            }
        }

        // Optimize xi
        for (const tryXi of [xi - 0.1 * stdDev, xi + 0.1 * stdDev, xi - 0.01 * stdDev, xi + 0.01 * stdDev]) {
            const ll = logLik(gamma, delta, tryXi, lambda);
            if (ll > bestLL) {
                xi = tryXi;
                bestLL = ll;
            }
        }

        // Optimize lambda (must be positive)
        for (const tryLambda of [lambda * 0.9, lambda * 1.1, lambda * 0.99, lambda * 1.01]) {
            if (tryLambda > 0.01) {
                const ll = logLik(gamma, delta, xi, tryLambda);
                if (ll > bestLL) {
                    lambda = tryLambda;
                    bestLL = ll;
                }
            }
        }

        // Check convergence
        if (Math.abs(bestLL - prevLL) < 1e-8) {
            converged = true;
            break;
        }
    }

    // Validate final parameters
    if (!isFinite(bestLL) || delta <= 0 || lambda <= 0) {
        return {
            params: { gamma, delta, xi, lambda },
            logLikelihood: bestLL,
            numParams: 4,
            cdf: (x) => johnsonSUCDF(x, gamma, delta, xi, lambda),
            pdf: (x) => johnsonSUPDF(x, gamma, delta, xi, lambda),
            ppf: (p) => johnsonSUPPF(p, gamma, delta, xi, lambda),
            diagnostics: {
                converged: false,
                reason: 'invalid_params',
                extremeParams: true
            }
        };
    }

    // Check for extreme parameters
    const extremeParams = Math.abs(gamma) > 10 || delta > 10 || delta < 0.05;

    return {
        params: { gamma, delta, xi, lambda },
        logLikelihood: bestLL,
        numParams: 4,
        cdf: (x) => johnsonSUCDF(x, gamma, delta, xi, lambda),
        pdf: (x) => johnsonSUPDF(x, gamma, delta, xi, lambda),
        ppf: (p) => johnsonSUPPF(p, gamma, delta, xi, lambda),
        diagnostics: {
            converged,
            reason: converged ? 'converged' : 'max_iterations',
            extremeParams,
            initialEstimates: { gamma: gamma_init, delta: delta_init, xi: xi_init, lambda: lambda_init }
        }
    };
}

/**
 * Fallback Johnson SU fitting using moment matching
 * Used when quantile-based method fails
 */
function fitJohnsonSUMoments(data, mean, stdDev, skewness, kurtosis) {
    const n = data.length;

    // Simple moment-based initial estimates
    // These are approximate and may need refinement
    let delta = 1.0;
    let gamma = -skewness * 0.5; // Approximate relationship

    // For heavy-tailed data, delta should be smaller
    if (kurtosis > 3) {
        delta = Math.max(0.5, 2 / Math.sqrt(kurtosis));
    }

    const lambda = stdDev;
    const xi = mean - lambda * Math.sinh(gamma / delta) * Math.exp(0.5 / (delta * delta));

    // Log-likelihood
    const logLik = (g, d, x, l) => {
        if (d <= 0 || l <= 0) return -Infinity;
        let ll = 0;
        for (let i = 0; i < n; i++) {
            const u = (data[i] - x) / l;
            const z = g + d * Math.asinh(u);
            const sqrtTerm = Math.sqrt(u * u + 1);
            if (sqrtTerm <= 0) return -Infinity;
            ll += Math.log(d) - Math.log(l) - 0.5 * Math.log(2 * Math.PI)
                  - Math.log(sqrtTerm) - 0.5 * z * z;
        }
        return ll;
    };

    const finalLL = logLik(gamma, delta, xi, lambda);

    return {
        params: { gamma, delta, xi, lambda },
        logLikelihood: finalLL,
        numParams: 4,
        cdf: (x) => johnsonSUCDF(x, gamma, delta, xi, lambda),
        pdf: (x) => johnsonSUPDF(x, gamma, delta, xi, lambda),
        ppf: (p) => johnsonSUPPF(p, gamma, delta, xi, lambda),
        diagnostics: {
            converged: false,
            reason: 'moment_fallback',
            extremeParams: Math.abs(gamma) > 10 || delta > 10 || delta < 0.05
        }
    };
}

/**
 * Fit Johnson SB distribution using MLE with known bounds
 * Transformation: z = gamma + delta * ln((x - L) / (U - x))
 *
 * @param {number[]} data - Array of numeric values
 * @param {number} L - Lower bound (must be < min(data))
 * @param {number} U - Upper bound (must be > max(data))
 * @returns {Object|null} Fit result with params {gamma, delta, L, U}
 */
function fitJohnsonSB(data, L, U) {
    const n = data.length;
    if (n < 10) {
        return null; // Need sufficient data for parameter estimation
    }

    const minX = Math.min(...data);
    const maxX = Math.max(...data);
    const tolerance = (maxX - minX) * 1e-6;

    // Validate bounds - all data must be strictly within (L, U)
    if (L >= minX - tolerance || U <= maxX + tolerance) {
        return {
            params: null,
            logLikelihood: -Infinity,
            numParams: 2, // gamma and delta (L, U are fixed)
            boundsError: true,
            errorMessage: `Data must be strictly within bounds (${L}, ${U}). Data range: [${minX.toFixed(4)}, ${maxX.toFixed(4)}]`
        };
    }

    // Transform data to y = (x - L) / (U - x)
    const yData = data.map(x => (x - L) / (U - x));

    // Check all y values are positive (should be if bounds are valid)
    if (yData.some(y => y <= 0 || !isFinite(y))) {
        return {
            params: null,
            logLikelihood: -Infinity,
            numParams: 2,
            boundsError: true,
            errorMessage: 'Invalid transformation: check bounds'
        };
    }

    // Transform to z = ln(y) - this should be approximately normal if SB fits well
    const zData = yData.map(y => Math.log(y));

    // MLE for gamma and delta: z should be ~ gamma + delta * N(0,1)
    // After transformation, z ~ N(gamma/delta, 1/delta^2)... wait, let me reconsider
    // Actually: z = gamma + delta * w where w ~ N(0,1)
    // So z ~ N(gamma, delta^2)... no wait, that's not right either
    //
    // The transformation is: z = gamma + delta * ln((x-L)/(U-x))
    // If X ~ SB, then gamma + delta * ln((X-L)/(U-X)) ~ N(0,1)
    // So ln((X-L)/(U-X)) ~ N(-gamma/delta, 1/delta^2)
    //
    // Therefore zData (= ln(y)) should be ~ N(-gamma/delta, 1/delta^2)
    // Let mu_z = mean(zData), sigma_z = std(zData)
    // Then: -gamma/delta = mu_z  =>  gamma = -delta * mu_z
    //       1/delta^2 = sigma_z^2  =>  delta = 1/sigma_z (taking positive root since delta > 0)

    const mean_z = zData.reduce((a, b) => a + b, 0) / n;
    const variance_z = zData.reduce((sum, z) => sum + Math.pow(z - mean_z, 2), 0) / n;
    const sigma_z = Math.sqrt(variance_z);

    if (sigma_z < 1e-10) {
        // All data essentially identical
        return null;
    }

    let delta = 1 / sigma_z;
    let gamma = -delta * mean_z;

    // Stability guards
    if (delta < 0.01 || delta > 100 || Math.abs(gamma) > 100) {
        return {
            params: { gamma, delta, L, U },
            logLikelihood: -Infinity,
            numParams: 2,
            diagnostics: {
                converged: false,
                reason: 'extreme_params',
                extremeParams: true
            }
        };
    }

    // Compute log-likelihood
    // f(x) = (delta / sqrt(2*pi)) * ((U-L) / ((x-L)(U-x))) * exp(-0.5 * z^2)
    // where z = gamma + delta * ln((x-L)/(U-x))
    let logLik = 0;
    for (let i = 0; i < n; i++) {
        const x = data[i];
        const z = gamma + delta * Math.log((x - L) / (U - x));
        const jacobian = (U - L) / ((x - L) * (U - x));
        const logPdf = Math.log(delta) - 0.5 * Math.log(2 * Math.PI) + Math.log(jacobian) - 0.5 * z * z;

        if (!isFinite(logPdf)) {
            return null;
        }
        logLik += logPdf;
    }

    // Verify parameters are valid
    if (!isFinite(logLik)) {
        return null;
    }

    return {
        params: { gamma, delta, L, U },
        logLikelihood: logLik,
        numParams: 2, // Only gamma and delta are estimated; L, U are fixed
        cdf: (x) => johnsonSBCDF(x, gamma, delta, L, U),
        pdf: (x) => johnsonSBPDF(x, gamma, delta, L, U),
        ppf: (p) => johnsonSBPPF(p, gamma, delta, L, U),
        diagnostics: {
            converged: true,
            reason: null,
            extremeParams: Math.abs(gamma) > 10 || delta > 10 || delta < 0.1
        }
    };
}

// ============================================================================
// 3-Parameter Distribution Fitting (with threshold/location parameter)
// ============================================================================

/**
 * Fit Lognormal 3P distribution using profile likelihood
 * Threshold (gamma) is constrained: gamma < min(data)
 * @param {number[]} data - Array of positive values
 * @returns {Object|null} Fit result with params {threshold, mu, sigma}
 */
function fitLognormal3P(data) {
    const n = data.length;
    const minX = Math.min(...data);
    const iqr = computeIQR(data);

    // Initial bounds for threshold search
    // Threshold must be < min(data), we search from a reasonable lower bound
    const lowerBound = minX - 2 * iqr;
    const upperBound = minX - 1e-6;

    if (upperBound <= lowerBound) {
        return null; // Cannot fit
    }

    // Profile likelihood: for each candidate threshold, fit 2P lognormal to shifted data
    // Use golden section search to find optimal threshold
    let bestThreshold = null;
    let bestLogLik = -Infinity;
    let bestMu = null;
    let bestSigma = null;

    const goldenRatio = (Math.sqrt(5) - 1) / 2;
    let a = lowerBound;
    let b = upperBound;
    let c = b - goldenRatio * (b - a);
    let d = a + goldenRatio * (b - a);

    const evaluateThreshold = (threshold) => {
        const shiftedData = data.map(x => x - threshold);
        if (shiftedData.some(x => x <= 0)) return { logLik: -Infinity };

        const logData = shiftedData.map(x => Math.log(x));
        const mu = logData.reduce((sum, x) => sum + x, 0) / n;
        const variance = logData.reduce((sum, x) => sum + Math.pow(x - mu, 2), 0) / n;
        const sigma = Math.sqrt(variance);

        if (sigma <= 0) return { logLik: -Infinity };

        // Log-likelihood for 3P lognormal
        const logLik = -n / 2 * Math.log(2 * Math.PI) - n / 2 * Math.log(variance)
                       - logData.reduce((sum, x) => sum + Math.pow(x - mu, 2), 0) / (2 * variance)
                       - logData.reduce((sum, x) => sum + x, 0); // Jacobian

        return { logLik, mu, sigma };
    };

    // Golden section search iterations
    for (let iter = 0; iter < 50; iter++) {
        const evalC = evaluateThreshold(c);
        const evalD = evaluateThreshold(d);

        if (evalC.logLik > evalD.logLik) {
            b = d;
            d = c;
            c = b - goldenRatio * (b - a);
        } else {
            a = c;
            c = d;
            d = a + goldenRatio * (b - a);
        }

        if (Math.abs(b - a) < 1e-8) break;
    }

    // Final evaluation at midpoint
    const finalThreshold = (a + b) / 2;
    const finalEval = evaluateThreshold(finalThreshold);

    if (finalEval.logLik === -Infinity) {
        return null;
    }

    bestThreshold = finalThreshold;
    bestLogLik = finalEval.logLik;
    bestMu = finalEval.mu;
    bestSigma = finalEval.sigma;

    // Check stability: threshold too close to min(data) is unstable
    const stabilityBound = minX - 0.5 * iqr;
    const isUnstable = bestThreshold > stabilityBound;

    return {
        params: { threshold: bestThreshold, mu: bestMu, sigma: bestSigma },
        logLikelihood: bestLogLik,
        numParams: 3,
        cdf: (x) => {
            if (x <= bestThreshold) return 0;
            return lognormalCDF(x - bestThreshold, bestMu, bestSigma);
        },
        pdf: (x) => {
            if (x <= bestThreshold) return 0;
            return lognormalPDF(x - bestThreshold, bestMu, bestSigma);
        },
        ppf: (p) => {
            if (p <= 0) return bestThreshold;
            if (p >= 1) return Infinity;
            return bestThreshold + lognormalPPF(p, bestMu, bestSigma);
        },
        isUnstable,
        stabilityBound
    };
}

/**
 * Fit Weibull 3P distribution using profile likelihood
 * Threshold (gamma) is constrained: gamma < min(data)
 */
function fitWeibull3P(data) {
    const n = data.length;
    const minX = Math.min(...data);
    const iqr = computeIQR(data);

    const lowerBound = minX - 2 * iqr;
    const upperBound = minX - 1e-6;

    if (upperBound <= lowerBound) {
        return null;
    }

    const goldenRatio = (Math.sqrt(5) - 1) / 2;
    let a = lowerBound;
    let b = upperBound;

    const evaluateThreshold = (threshold) => {
        const shiftedData = data.map(x => x - threshold);
        if (shiftedData.some(x => x <= 0)) return { logLik: -Infinity };

        // Fit 2P Weibull to shifted data
        const logData = shiftedData.map(x => Math.log(x));
        const sumLog = logData.reduce((sum, x) => sum + x, 0);

        // Initial estimate for shape
        const mean = shiftedData.reduce((sum, x) => sum + x, 0) / n;
        const variance = shiftedData.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;
        const cv = Math.sqrt(variance) / mean;
        let shape = Math.pow(cv, -1.086);

        // Newton-Raphson for shape
        for (let iter = 0; iter < 30; iter++) {
            const sumXk = shiftedData.reduce((sum, x) => sum + Math.pow(x, shape), 0);
            const sumXkLogX = shiftedData.reduce((sum, x) => sum + Math.pow(x, shape) * Math.log(x), 0);

            const f = sumXkLogX / sumXk - 1 / shape - sumLog / n;
            const sumXkLogX2 = shiftedData.reduce((sum, x) => sum + Math.pow(x, shape) * Math.pow(Math.log(x), 2), 0);
            const fPrime = (sumXkLogX2 * sumXk - sumXkLogX * sumXkLogX) / (sumXk * sumXk) + 1 / (shape * shape);

            const delta = f / fPrime;
            shape -= delta;

            if (shape <= 0) shape = 0.1;
            if (Math.abs(delta) < 1e-8) break;
        }

        const scale = Math.pow(shiftedData.reduce((sum, x) => sum + Math.pow(x, shape), 0) / n, 1 / shape);

        // Log-likelihood
        const logLik = n * Math.log(shape) - n * shape * Math.log(scale)
                       + (shape - 1) * sumLog
                       - shiftedData.reduce((sum, x) => sum + Math.pow(x / scale, shape), 0);

        return { logLik, shape, scale };
    };

    // Golden section search
    let c = b - goldenRatio * (b - a);
    let d = a + goldenRatio * (b - a);

    for (let iter = 0; iter < 50; iter++) {
        const evalC = evaluateThreshold(c);
        const evalD = evaluateThreshold(d);

        if (evalC.logLik > evalD.logLik) {
            b = d;
            d = c;
            c = b - goldenRatio * (b - a);
        } else {
            a = c;
            c = d;
            d = a + goldenRatio * (b - a);
        }

        if (Math.abs(b - a) < 1e-8) break;
    }

    const finalThreshold = (a + b) / 2;
    const finalEval = evaluateThreshold(finalThreshold);

    if (finalEval.logLik === -Infinity) {
        return null;
    }

    const stabilityBound = minX - 0.5 * iqr;
    const isUnstable = finalThreshold > stabilityBound;
    const finalShape = finalEval.shape;
    const finalScale = finalEval.scale;

    return {
        params: { threshold: finalThreshold, shape: finalShape, scale: finalScale },
        logLikelihood: finalEval.logLik,
        numParams: 3,
        cdf: (x) => {
            if (x <= finalThreshold) return 0;
            return weibullCDF(x - finalThreshold, finalShape, finalScale);
        },
        pdf: (x) => {
            if (x <= finalThreshold) return 0;
            return weibullPDF(x - finalThreshold, finalShape, finalScale);
        },
        ppf: (p) => {
            if (p <= 0) return finalThreshold;
            if (p >= 1) return Infinity;
            return finalThreshold + weibullPPF(p, finalShape, finalScale);
        },
        isUnstable,
        stabilityBound
    };
}

/**
 * Fit Gamma 3P distribution using profile likelihood
 * Threshold (gamma) is constrained: gamma < min(data)
 */
function fitGamma3P(data) {
    const n = data.length;
    const minX = Math.min(...data);
    const iqr = computeIQR(data);

    const lowerBound = minX - 2 * iqr;
    const upperBound = minX - 1e-6;

    if (upperBound <= lowerBound) {
        return null;
    }

    const goldenRatio = (Math.sqrt(5) - 1) / 2;
    let a = lowerBound;
    let b = upperBound;

    const evaluateThreshold = (threshold) => {
        const shiftedData = data.map(x => x - threshold);
        if (shiftedData.some(x => x <= 0)) return { logLik: -Infinity };

        // Fit 2P Gamma to shifted data
        const mean = shiftedData.reduce((sum, x) => sum + x, 0) / n;
        const sumLog = shiftedData.reduce((sum, x) => sum + Math.log(x), 0);
        const s = Math.log(mean) - sumLog / n;

        // Initial estimate using Minka's approximation
        let shape = (3 - s + Math.sqrt(Math.pow(s - 3, 2) + 24 * s)) / (12 * s);

        // Newton-Raphson for shape
        for (let iter = 0; iter < 30; iter++) {
            const psi = Math.log(shape) - 1 / (2 * shape) - 1 / (12 * shape * shape);
            const psiPrime = 1 / shape + 1 / (2 * shape * shape) + 1 / (6 * Math.pow(shape, 3));

            const f = Math.log(shape) - psi - s;
            const fPrime = 1 / shape - psiPrime;

            const delta = f / fPrime;
            shape -= delta;

            if (shape <= 0) shape = 0.1;
            if (Math.abs(delta) < 1e-8) break;
        }

        const rate = shape / mean;

        // Log-likelihood
        const logLik = n * shape * Math.log(rate) - n * lnGamma(shape)
                       + (shape - 1) * sumLog - rate * shiftedData.reduce((sum, x) => sum + x, 0);

        return { logLik, shape, rate };
    };

    // Golden section search
    let c = b - goldenRatio * (b - a);
    let d = a + goldenRatio * (b - a);

    for (let iter = 0; iter < 50; iter++) {
        const evalC = evaluateThreshold(c);
        const evalD = evaluateThreshold(d);

        if (evalC.logLik > evalD.logLik) {
            b = d;
            d = c;
            c = b - goldenRatio * (b - a);
        } else {
            a = c;
            c = d;
            d = a + goldenRatio * (b - a);
        }

        if (Math.abs(b - a) < 1e-8) break;
    }

    const finalThreshold = (a + b) / 2;
    const finalEval = evaluateThreshold(finalThreshold);

    if (finalEval.logLik === -Infinity) {
        return null;
    }

    const stabilityBound = minX - 0.5 * iqr;
    const isUnstable = finalThreshold > stabilityBound;
    const finalShape = finalEval.shape;
    const finalRate = finalEval.rate;

    return {
        params: { threshold: finalThreshold, shape: finalShape, rate: finalRate },
        logLikelihood: finalEval.logLik,
        numParams: 3,
        cdf: (x) => {
            if (x <= finalThreshold) return 0;
            return gammaCDF(x - finalThreshold, finalShape, finalRate);
        },
        pdf: (x) => {
            if (x <= finalThreshold) return 0;
            return gammaPDF(x - finalThreshold, finalShape, finalRate);
        },
        ppf: (p) => {
            if (p <= 0) return finalThreshold;
            if (p >= 1) return Infinity;
            return finalThreshold + gammaPPF(p, finalShape, finalRate);
        },
        isUnstable,
        stabilityBound
    };
}

// ============================================================================
// Likelihood Ratio Test (LRT)
// ============================================================================

/**
 * Compute Likelihood Ratio Test statistic and p-value
 * For nested models: LRT = 2 * (LL_full - LL_reduced)
 * Under H0, LRT ~ chi-square(df = difference in parameters)
 *
 * @param {number} logLik3P - Log-likelihood of 3P model
 * @param {number} logLik2P - Log-likelihood of 2P model
 * @param {number} df - Degrees of freedom (default 1 for 3P vs 2P)
 * @returns {Object} LRT statistic and p-value
 */
function computeLRT(logLik3P, logLik2P, df = 1) {
    const lrtStatistic = 2 * (logLik3P - logLik2P);

    // P-value from chi-square distribution
    // Small p-value means 3P is significantly better
    const pValue = 1 - chiSquareCDF(Math.max(0, lrtStatistic), df);

    return {
        statistic: lrtStatistic,
        pValue: Math.max(0, Math.min(1, pValue)),
        df
    };
}

// ============================================================================
// Anderson-Darling Test
// ============================================================================

/**
 * Compute Anderson-Darling statistic
 */
function computeAD(data, cdf) {
    const n = data.length;
    const sorted = [...data].sort((a, b) => a - b);

    let sum = 0;
    for (let i = 0; i < n; i++) {
        const Fi = cdf(sorted[i]);
        const Fn_i = cdf(sorted[n - 1 - i]);

        // Avoid log(0) issues
        const logFi = Fi > 1e-10 ? Math.log(Fi) : Math.log(1e-10);
        const log1mFn_i = (1 - Fn_i) > 1e-10 ? Math.log(1 - Fn_i) : Math.log(1e-10);

        sum += (2 * (i + 1) - 1) * (logFi + log1mFn_i);
    }

    return -n - sum / n;
}

/**
 * Compute p-value for Normal AD statistic
 * Using D'Agostino & Stephens (1986) approximation
 */
function normalADPValue(ad, n) {
    // Adjust AD for sample size
    const adStar = ad * (1 + 0.75 / n + 2.25 / (n * n));

    // P-value approximation
    if (adStar >= 0.6) {
        return Math.exp(1.2937 - 5.709 * adStar + 0.0186 * adStar * adStar);
    } else if (adStar >= 0.34) {
        return Math.exp(0.9177 - 4.279 * adStar - 1.38 * adStar * adStar);
    } else if (adStar >= 0.2) {
        return 1 - Math.exp(-8.318 + 42.796 * adStar - 59.938 * adStar * adStar);
    } else {
        return 1 - Math.exp(-13.436 + 101.14 * adStar - 223.73 * adStar * adStar);
    }
}

/**
 * Compute p-value for Exponential AD statistic
 */
function exponentialADPValue(ad, n) {
    const adStar = ad * (1 + 0.6 / n);

    if (adStar >= 0.95) {
        return Math.exp(0.731 - 3.009 * adStar + 0.15 * adStar * adStar);
    } else if (adStar >= 0.51) {
        return Math.exp(0.9209 - 3.353 * adStar + 0.3 * adStar * adStar);
    } else if (adStar >= 0.26) {
        return 1 - Math.exp(-6.1327 + 20.218 * adStar - 18.663 * adStar * adStar);
    } else {
        return 1 - Math.exp(-12.2204 + 67.459 * adStar - 110.3 * adStar * adStar);
    }
}

/**
 * Compute p-value for Weibull AD statistic
 * Uses the CMH-17-1G / Lawless (1982) logistic formula.
 * References:
 *   - Lawless, J.F. (1982). Statistical Models and Methods for Lifetime Data.
 *   - CMH-17-1G (2012). Composite Materials Handbook, Vol. 1, SAE International.
 *   - cmstatr R package (validates against Lawless 1982 tabulated values).
 * Validated: matches Stephens (1977) table at all published significance
 * levels from p=0.25 down to p=0.01 to within 0.0003.
 */
function weibullADPValue(ad, n) {
    // Minitab-calibrated logistic fit for the Weibull AD* p-value — verified against
    // the Minitab-sourced distribution_fit fixtures (right_skewed weibull ad=1.266 →
    // 0.00234 vs Minitab 0.00233; gamma_like ad=0.461 → 0.250 vs Minitab 0.25;
    // outlier_stress weibull3p ad=0.839 → 0.027 vs Minitab 0.02). Do NOT replace with
    // the generic piecewise D'Agostino-Stephens form — it does NOT match Minitab here
    // (it gives 0.146 where Minitab reports 0.002). See FIX-5 / engineParity.test.js.
    const adStar = (1 + 0.2 / Math.sqrt(n)) * ad;
    return 1 / (1 + Math.exp(-0.1 + 1.24 * Math.log(adStar) + 4.48 * adStar));
}

/**
 * Compute p-value for Gamma AD statistic
 * Approximate using modified exponential p-values
 */
function gammaADPValue(ad, n, shape) {
    // For gamma, use exponential-like approximation adjusted by shape
    // This is an approximation; exact tables are complex
    const adStar = ad * (1 + 0.6 / n);

    // For shape close to 1, use exponential approximation
    // For larger shapes, gamma approaches normal behavior
    const blend = Math.min(shape, 5) / 5;
    const expP = exponentialADPValue(ad, n);
    const normP = normalADPValue(ad, n);

    return (1 - blend) * expP + blend * normP;
}

/**
 * Compute p-value for Johnson SU AD statistic
 * Since Johnson SU transforms data to standard normal via z = gamma + delta * asinh((x-xi)/lambda),
 * we use the normal AD p-value approximation on the transformed data.
 * This is a reasonable approximation since the transformation yields normality under H0.
 */
function johnsonSUADPValue(ad, n) {
    // Johnson SU transforms to normal, so use normal AD p-value with adjustment
    // The 4-parameter estimation affects the distribution; apply a correction
    const adStar = ad * (1 + 0.75 / n + 2.25 / (n * n));

    // Use normal AD p-value approximation (conservative for 4-parameter model)
    if (adStar >= 0.6) {
        return Math.exp(1.2937 - 5.709 * adStar + 0.0186 * adStar * adStar);
    } else if (adStar >= 0.34) {
        return Math.exp(0.9177 - 4.279 * adStar - 1.38 * adStar * adStar);
    } else if (adStar >= 0.2) {
        return 1 - Math.exp(-8.318 + 42.796 * adStar - 59.938 * adStar * adStar);
    } else {
        return 1 - Math.exp(-13.436 + 101.14 * adStar - 223.73 * adStar * adStar);
    }
}

/**
 * Johnson SB Anderson-Darling p-value
 * Like Johnson SU, SB transforms to standard normal, so we use normal AD p-value
 * Only 2 parameters (gamma, delta) estimated since bounds L, U are fixed
 */
function johnsonSBADPValue(ad, n) {
    // With 2 estimated parameters (gamma, delta), use normal AD p-value adjustment
    const adStar = ad * (1 + 0.75 / n + 2.25 / (n * n));

    // Use normal AD p-value approximation
    if (adStar >= 0.6) {
        return Math.exp(1.2937 - 5.709 * adStar + 0.0186 * adStar * adStar);
    } else if (adStar >= 0.34) {
        return Math.exp(0.9177 - 4.279 * adStar - 1.38 * adStar * adStar);
    } else if (adStar >= 0.2) {
        return 1 - Math.exp(-8.318 + 42.796 * adStar - 59.938 * adStar * adStar);
    } else {
        return 1 - Math.exp(-13.436 + 101.14 * adStar - 223.73 * adStar * adStar);
    }
}

// ============================================================================
// Eligibility Status for Conditional Distributions
// ============================================================================

/**
 * Eligibility status for conditional/advanced distributions
 * @readonly
 * @enum {string}
 */
export const ELIGIBILITY_STATUS = {
    /** Distribution is fully eligible for recommendation */
    ELIGIBLE: 'eligible',
    /** Distribution requires user confirmation (e.g., bounds) to be eligible */
    ELIGIBLE_WITH_CONFIRMATION: 'eligible_with_confirmation',
    /** Distribution is not eligible (requirements not met) */
    INELIGIBLE: 'ineligible'
};

// ============================================================================
// Information Criteria
// ============================================================================

/**
 * Compute AIC (Akaike Information Criterion)
 */
function computeAIC(logLikelihood, numParams) {
    return 2 * numParams - 2 * logLikelihood;
}

/**
 * Compute BIC (Bayesian Information Criterion)
 */
function computeBIC(logLikelihood, numParams, n) {
    return numParams * Math.log(n) - 2 * logLikelihood;
}

// ============================================================================
// Main Distribution Fitting Interface
// ============================================================================

/**
 * Distribution definitions for Phase 3
 * 2-parameter distributions + 3-parameter variants with threshold
 */
export const DISTRIBUTIONS = {
    normal: {
        id: 'normal',
        name: 'Normal',
        shortName: 'Normal',
        fit: fitNormal,
        pValue: normalADPValue,
        requiresPositive: false,
        numParams: 2,
        is3P: false
    },
    lognormal: {
        id: 'lognormal',
        name: 'Lognormal',
        shortName: 'Lognormal',
        fit: fitLognormal,
        pValue: normalADPValue, // Lognormal uses normal AD on log-transformed data
        requiresPositive: true,
        numParams: 2,
        is3P: false,
        variant3P: 'lognormal3p'
    },
    lognormal3p: {
        id: 'lognormal3p',
        name: 'Lognormal (3P)',
        shortName: '3P Lognormal',
        fit: fitLognormal3P,
        pValue: normalADPValue,
        requiresPositive: false, // 3P can handle any data (shifts it positive)
        numParams: 3,
        is3P: true,
        baseDistribution: 'lognormal',
        parameterLabel: 'threshold'
    },
    weibull: {
        id: 'weibull',
        name: 'Weibull',
        shortName: 'Weibull',
        fit: fitWeibull,
        pValue: weibullADPValue,
        requiresPositive: true,
        numParams: 2,
        is3P: false,
        variant3P: 'weibull3p'
    },
    weibull3p: {
        id: 'weibull3p',
        name: 'Weibull (3P)',
        shortName: '3P Weibull',
        fit: fitWeibull3P,
        pValue: weibullADPValue,
        requiresPositive: false,
        numParams: 3,
        is3P: true,
        baseDistribution: 'weibull',
        parameterLabel: 'threshold'
    },
    gamma: {
        id: 'gamma',
        name: 'Gamma',
        shortName: 'Gamma',
        fit: fitGamma,
        pValue: gammaADPValue,
        requiresPositive: true,
        numParams: 2,
        is3P: false,
        variant3P: 'gamma3p'
    },
    gamma3p: {
        id: 'gamma3p',
        name: 'Gamma (3P)',
        shortName: '3P Gamma',
        fit: fitGamma3P,
        pValue: gammaADPValue,
        requiresPositive: false,
        numParams: 3,
        is3P: true,
        baseDistribution: 'gamma',
        parameterLabel: 'threshold'
    },
    exponential: {
        id: 'exponential',
        name: 'Exponential',
        shortName: 'Exponential',
        fit: fitExponential,
        pValue: exponentialADPValue,
        requiresPositive: true,
        numParams: 1,
        is3P: false
    },
    johnsonsu: {
        id: 'johnsonsu',
        name: 'Johnson SU',
        shortName: 'Johnson SU',
        fit: fitJohnsonSU,
        pValue: johnsonSUADPValue,
        requiresPositive: false,  // Johnson SU can fit any data (unbounded support)
        numParams: 4,
        is3P: false,  // Not part of 2P/3P family - no LRT comparison
        family: 'johnsonsu',  // Standalone family - no 2P/3P variants
        description: 'Flexible 4-parameter distribution for skewed/heavy-tailed data'
    },
    johnsonsb: {
        id: 'johnsonsb',
        name: 'Johnson SB',
        shortName: 'Johnson SB',
        fit: fitJohnsonSB,  // Note: requires (data, L, U) parameters
        pValue: johnsonSBADPValue,
        requiresPositive: false,  // Bounded distribution - works with any data in bounds
        requiresBounds: true,     // Conditional distribution - requires L, U bounds
        numParams: 2,  // Only gamma and delta are estimated (L, U are fixed inputs)
        is3P: false,  // Not part of 2P/3P family - no LRT comparison
        family: 'johnsonsb',  // Standalone family - no 2P/3P variants
        description: 'Bounded 4-parameter distribution for data with known bounds'
    }
};

/**
 * Get display-friendly parameter labels and derived values for a distribution.
 * This provides a uniform mapping layer for UI display across all distributions.
 *
 * @param {string} distId - Distribution ID (e.g., 'normal', 'lognormal', 'weibull')
 * @param {Object} params - The raw params object from the fit result
 * @returns {Object} Display-friendly parameter mapping with labels and derived values
 */
export function getParameterDisplayMapping(distId, params) {
    if (!params) return null;

    const mapping = {
        distId,
        distName: DISTRIBUTIONS[distId]?.name || distId,
        params: [],      // Array of { key, label, value, description }
        derived: []      // Array of derived/computed values for display
    };

    switch (distId) {
        case 'normal':
            mapping.params = [
                { key: 'mu', label: 'μ (mean)', value: params.mu, description: 'Location parameter' },
                { key: 'sigma', label: 'σ (std dev)', value: params.sigma, description: 'Scale parameter' }
            ];
            break;

        case 'lognormal':
            mapping.params = [
                { key: 'mu', label: 'μ_log', value: params.mu, description: 'Mean of ln(X)' },
                { key: 'sigma', label: 'σ_log', value: params.sigma, description: 'Std dev of ln(X)' }
            ];
            // Derived values for intuitive interpretation
            mapping.derived = [
                { key: 'scale', label: 'Scale (median)', value: Math.exp(params.mu), description: 'exp(μ_log) = median of X' },
                { key: 'geomMean', label: 'Geometric Mean', value: Math.exp(params.mu), description: 'Same as scale for lognormal' }
            ];
            break;

        case 'lognormal3p':
            mapping.params = [
                { key: 'threshold', label: 'θ (threshold)', value: params.threshold, description: 'Location shift' },
                { key: 'mu', label: 'μ_log', value: params.mu, description: 'Mean of ln(X-θ)' },
                { key: 'sigma', label: 'σ_log', value: params.sigma, description: 'Std dev of ln(X-θ)' }
            ];
            mapping.derived = [
                { key: 'scale', label: 'Scale', value: Math.exp(params.mu), description: 'exp(μ_log)' },
                { key: 'effectiveMedian', label: 'Median', value: params.threshold + Math.exp(params.mu), description: 'θ + exp(μ_log)' }
            ];
            break;

        case 'weibull':
            mapping.params = [
                { key: 'shape', label: 'β (shape)', value: params.shape, description: 'Shape parameter' },
                { key: 'scale', label: 'η (scale)', value: params.scale, description: 'Scale/characteristic life' }
            ];
            // Weibull median = scale * (ln(2))^(1/shape)
            mapping.derived = [
                { key: 'median', label: 'Median', value: params.scale * Math.pow(Math.log(2), 1 / params.shape), description: 'η × ln(2)^(1/β)' }
            ];
            break;

        case 'weibull3p':
            mapping.params = [
                { key: 'threshold', label: 'γ (threshold)', value: params.threshold, description: 'Location shift' },
                { key: 'shape', label: 'β (shape)', value: params.shape, description: 'Shape parameter' },
                { key: 'scale', label: 'η (scale)', value: params.scale, description: 'Scale parameter' }
            ];
            mapping.derived = [
                { key: 'median', label: 'Median', value: params.threshold + params.scale * Math.pow(Math.log(2), 1 / params.shape), description: 'γ + η × ln(2)^(1/β)' }
            ];
            break;

        case 'gamma':
            mapping.params = [
                { key: 'shape', label: 'α (shape)', value: params.shape, description: 'Shape parameter' },
                { key: 'rate', label: 'β (rate)', value: params.rate, description: 'Rate = 1/scale' }
            ];
            mapping.derived = [
                { key: 'scale', label: 'Scale (1/rate)', value: 1 / params.rate, description: 'θ = 1/β' },
                { key: 'mean', label: 'Mean', value: params.shape / params.rate, description: 'α/β' }
            ];
            break;

        case 'gamma3p':
            mapping.params = [
                { key: 'threshold', label: 'γ (threshold)', value: params.threshold, description: 'Location shift' },
                { key: 'shape', label: 'α (shape)', value: params.shape, description: 'Shape parameter' },
                { key: 'rate', label: 'β (rate)', value: params.rate, description: 'Rate = 1/scale' }
            ];
            mapping.derived = [
                { key: 'scale', label: 'Scale (1/rate)', value: 1 / params.rate, description: 'θ = 1/β' },
                { key: 'mean', label: 'Mean', value: params.threshold + params.shape / params.rate, description: 'γ + α/β' }
            ];
            break;

        case 'exponential':
            mapping.params = [
                { key: 'rate', label: 'λ (rate)', value: params.rate, description: 'Rate parameter' }
            ];
            mapping.derived = [
                { key: 'mean', label: 'Mean (1/λ)', value: 1 / params.rate, description: 'Expected value' },
                { key: 'median', label: 'Median', value: Math.log(2) / params.rate, description: 'ln(2)/λ' }
            ];
            break;

        case 'johnsonsu':
            mapping.params = [
                { key: 'gamma', label: 'γ (shape)', value: params.gamma, description: 'Skewness parameter' },
                { key: 'delta', label: 'δ (shape)', value: params.delta, description: 'Kurtosis parameter (δ > 0)' },
                { key: 'xi', label: 'ξ (location)', value: params.xi, description: 'Location parameter' },
                { key: 'lambda', label: 'λ (scale)', value: params.lambda, description: 'Scale parameter (λ > 0)' }
            ];
            break;

        case 'johnsonsb':
            mapping.params = [
                { key: 'gamma', label: 'γ (shape)', value: params.gamma, description: 'Skewness parameter' },
                { key: 'delta', label: 'δ (shape)', value: params.delta, description: 'Kurtosis parameter (δ > 0)' },
                { key: 'L', label: 'L (lower)', value: params.L, description: 'Lower bound' },
                { key: 'U', label: 'U (upper)', value: params.U, description: 'Upper bound' }
            ];
            mapping.derived = [
                { key: 'range', label: 'Range', value: params.U - params.L, description: 'U - L' }
            ];
            break;

        default:
            // Generic fallback: just list the params with their keys
            mapping.params = Object.entries(params).map(([key, value]) => ({
                key,
                label: key,
                value,
                description: ''
            }));
    }

    return mapping;
}

/**
 * Fit all distributions to data and compute GOF metrics
 * @param {number[]} data - Array of numeric values
 * @param {number} alpha - Significance level (default 0.05)
 * @param {Object} boundsConfig - Configuration for bounded distributions (optional)
 * @param {boolean} boundsConfig.boundsConfirmed - Whether bounds have been confirmed by user
 * @param {number} boundsConfig.lowerBound - Lower bound L for Johnson SB
 * @param {number} boundsConfig.upperBound - Upper bound U for Johnson SB
 * @returns {Object} Distribution fitting results
 */
export function fitAllDistributions(data, alpha = 0.05, boundsConfig = null) {
    // Filter to valid numeric data
    const validData = data.filter(x => typeof x === 'number' && !isNaN(x) && isFinite(x));
    const n = validData.length;

    if (n < 3) {
        return {
            results: [],
            recommendation: null,
            hasPositiveOnly: false,
            n: n
        };
    }

    const hasNonPositive = validData.some(x => x <= 0);
    const results = [];
    const fitResults = {}; // Store fit results for LRT computation

    // Extract bounds config
    const boundsConfirmed = boundsConfig?.boundsConfirmed || false;
    const lowerBound = boundsConfig?.lowerBound;
    const upperBound = boundsConfig?.upperBound;

    // Fit each distribution
    for (const [id, dist] of Object.entries(DISTRIBUTIONS)) {
        const result = {
            id: id,
            name: dist.name,
            shortName: dist.shortName,
            numParams: dist.numParams,
            is3P: dist.is3P || false,
            baseDistribution: dist.baseDistribution || null,
            fit: null,
            ad: null,
            pValue: null,
            aic: null,
            bic: null,
            status: 'unknown',
            canFit: true,
            params: null,
            // 3P specific fields
            isUnstable: false,
            lrt: null,
            lrtPValue: null,
            prefer3P: null, // true = '3P justified', false = 'Prefer 2P', null = N/A
            // Eligibility status for conditional distributions
            eligibilityStatus: ELIGIBILITY_STATUS.ELIGIBLE,
            requiresBounds: dist.requiresBounds || false
        };

        // Check if distribution can be fit
        if (dist.requiresPositive && hasNonPositive) {
            result.canFit = false;
            result.status = 'not_applicable';
            result.statusReason = 'Requires positive data';
            results.push(result);
            continue;
        }

        // Handle bounded distributions (Johnson SB)
        if (dist.requiresBounds) {
            if (!boundsConfirmed) {
                // Bounds not confirmed - show as requiring confirmation
                result.eligibilityStatus = ELIGIBILITY_STATUS.ELIGIBLE_WITH_CONFIRMATION;
                result.canFit = false;
                result.status = 'awaiting_bounds';
                result.statusReason = 'Requires bounds confirmation (L, U)';
                results.push(result);
                continue;
            }

            // Bounds confirmed - validate them
            if (lowerBound == null || upperBound == null || lowerBound >= upperBound) {
                result.eligibilityStatus = ELIGIBILITY_STATUS.INELIGIBLE;
                result.canFit = false;
                result.status = 'not_applicable';
                result.statusReason = 'Invalid bounds: L must be less than U';
                results.push(result);
                continue;
            }

            const minX = Math.min(...validData);
            const maxX = Math.max(...validData);
            const tolerance = (maxX - minX) * 1e-6;

            // Validate all data is strictly within bounds
            if (lowerBound >= minX - tolerance || upperBound <= maxX + tolerance) {
                result.eligibilityStatus = ELIGIBILITY_STATUS.INELIGIBLE;
                result.canFit = false;
                result.status = 'not_applicable';
                result.statusReason = `Data must be strictly within (${lowerBound}, ${upperBound}). Data range: [${minX.toFixed(4)}, ${maxX.toFixed(4)}]`;
                results.push(result);
                continue;
            }

            // Bounds are valid - mark as eligible
            result.eligibilityStatus = ELIGIBILITY_STATUS.ELIGIBLE;
        }

        try {
            // Special handling for Johnson SB which needs bounds
            let fitResult;
            if (id === 'johnsonsb') {
                fitResult = dist.fit(validData, lowerBound, upperBound);
            } else {
                fitResult = dist.fit(validData);
            }

            if (!fitResult) {
                result.canFit = false;
                result.status = 'fit_failed';
                result.statusReason = 'Fitting failed';
                results.push(result);
                continue;
            }

            result.fit = fitResult;
            result.params = fitResult.params;
            fitResults[id] = fitResult; // Store for LRT

            // Handle 3P stability
            if (dist.is3P && fitResult.isUnstable) {
                result.isUnstable = true;
            }

            // Handle Johnson SU diagnostics
            if (id === 'johnsonsu' && fitResult.diagnostics) {
                result.diagnostics = fitResult.diagnostics;
                // Mark as unstable if convergence failed or extreme params
                if (!fitResult.diagnostics.converged || fitResult.diagnostics.extremeParams) {
                    result.isUnstable = true;
                    result.statusReason = fitResult.diagnostics.extremeParams
                        ? 'Extreme parameter values'
                        : 'Fitting did not converge';
                }
            }

            // Handle Johnson SB diagnostics
            if (id === 'johnsonsb' && fitResult.diagnostics) {
                result.diagnostics = fitResult.diagnostics;
                // Mark as unstable if convergence failed or extreme params
                if (!fitResult.diagnostics.converged || fitResult.diagnostics.extremeParams) {
                    result.isUnstable = true;
                    result.statusReason = fitResult.diagnostics.extremeParams
                        ? 'Extreme parameter values'
                        : 'Fitting did not converge';
                }
            }

            // Handle Johnson SB bounds error
            if (id === 'johnsonsb' && fitResult.boundsError) {
                result.canFit = false;
                result.status = 'not_applicable';
                result.statusReason = fitResult.errorMessage || 'Bounds validation failed';
                result.eligibilityStatus = ELIGIBILITY_STATUS.INELIGIBLE;
                results.push(result);
                continue;
            }

            // Compute AD statistic
            const ad = computeAD(validData, fitResult.cdf);
            result.ad = ad;

            // Compute p-value
            if (id === 'johnsonsu') {
                // For Johnson SU, compute AD on transformed data (z-values)
                // z = gamma + delta * asinh((x - xi) / lambda) should be ~ N(0,1)
                const { gamma: g, delta: d, xi, lambda: l } = fitResult.params;
                const zData = validData.map(x => g + d * Math.asinh((x - xi) / l));
                // z-values should be approximately N(0,1) if fit is good
                const zAD = computeAD(zData, (z) => normalCDF(z));
                result.ad = zAD;
                result.pValue = normalADPValue(zAD, n);
            } else if (id === 'johnsonsb') {
                // For Johnson SB, compute AD on transformed data (z-values)
                // z = gamma + delta * ln((x - L) / (U - x)) should be ~ N(0,1)
                const { gamma: g, delta: d, L, U } = fitResult.params;
                const zData = validData.map(x => g + d * Math.log((x - L) / (U - x)));
                // z-values should be approximately N(0,1) if fit is good
                const zAD = computeAD(zData, (z) => normalCDF(z));
                result.ad = zAD;
                result.pValue = normalADPValue(zAD, n);
            } else if (id === 'gamma' || id === 'gamma3p') {
                result.pValue = dist.pValue(ad, n, fitResult.params.shape);
            } else if (id === 'lognormal') {
                // For 2P lognormal, compute AD on log-transformed data
                const logData = validData.map(x => Math.log(x));
                const logFit = fitNormal(logData);
                const logAD = computeAD(logData, logFit.cdf);
                result.ad = logAD;
                result.pValue = normalADPValue(logAD, n);
            } else if (id === 'lognormal3p') {
                // For 3P lognormal, compute AD on log-transformed shifted data
                const threshold = fitResult.params.threshold;
                const shiftedData = validData.map(x => x - threshold);
                if (shiftedData.every(x => x > 0)) {
                    const logData = shiftedData.map(x => Math.log(x));
                    const logFit = fitNormal(logData);
                    const logAD = computeAD(logData, logFit.cdf);
                    result.ad = logAD;
                    result.pValue = normalADPValue(logAD, n);
                } else {
                    result.pValue = dist.pValue(ad, n);
                }
            } else {
                result.pValue = dist.pValue(ad, n);
            }

            // Clamp p-value to [0, 1]
            result.pValue = Math.max(0, Math.min(1, result.pValue));

            // Compute AIC and BIC
            result.aic = computeAIC(fitResult.logLikelihood, fitResult.numParams);
            result.bic = computeBIC(fitResult.logLikelihood, fitResult.numParams, n);

            // Determine status
            if (result.isUnstable) {
                result.status = 'unstable';
                result.statusReason = 'Threshold too close to data minimum';
            } else if (result.pValue >= alpha) {
                result.status = 'acceptable';
            } else {
                result.status = 'rejected';
            }

        } catch (error) {
            result.canFit = false;
            result.status = 'fit_failed';
            result.statusReason = error.message;
        }

        results.push(result);
    }

    // Compute LRT for 3P vs 2P comparisons
    for (const result of results) {
        if (result.is3P && result.canFit && result.fit && result.baseDistribution) {
            const baseResult = results.find(r => r.id === result.baseDistribution);
            if (baseResult && baseResult.canFit && baseResult.fit) {
                const lrt = computeLRT(
                    result.fit.logLikelihood,
                    baseResult.fit.logLikelihood,
                    1 // df = 1 for one additional parameter
                );
                result.lrt = lrt.statistic;
                result.lrtPValue = lrt.pValue;

                // Determine if 3P is justified
                // 3P is justified if LRT p-value < alpha (significant improvement)
                if (result.status !== 'unstable') {
                    result.prefer3P = lrt.pValue < alpha;
                } else {
                    result.prefer3P = false; // Unstable fits should prefer 2P
                }
            }
        }
    }

    // Compute delta AIC and BIC
    const fittedResults = results.filter(r => r.aic !== null);
    if (fittedResults.length > 0) {
        const minAIC = Math.min(...fittedResults.map(r => r.aic));
        const minBIC = Math.min(...fittedResults.map(r => r.bic));

        for (const r of results) {
            if (r.aic !== null) {
                r.deltaAIC = r.aic - minAIC;
                r.deltaBIC = r.bic - minBIC;
            }
        }
    }

    // Sort results: acceptable first (by descending p-value, then ascending deltaAIC),
    // then rejected (by ascending AD), then unstable, then not applicable
    results.sort((a, b) => {
        // Status priority: acceptable > rejected > unstable > not_applicable/fit_failed
        const statusOrder = { acceptable: 0, rejected: 1, awaiting_bounds: 2, unstable: 3, not_applicable: 4, fit_failed: 5, unknown: 6 };
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;

        // Within acceptable: sort by descending p-value, then ascending deltaAIC
        if (a.status === 'acceptable') {
            if (a.pValue !== b.pValue) return b.pValue - a.pValue;
            return (a.deltaAIC || 0) - (b.deltaAIC || 0);
        }

        // Within rejected: sort by ascending AD
        if (a.status === 'rejected') {
            return (a.ad || Infinity) - (b.ad || Infinity);
        }

        return 0;
    });

    // Generate recommendation
    const recommendation = generateRecommendation(results, alpha);

    return {
        results,
        recommendation,
        hasNonPositive,
        n,
        alpha
    };
}

/**
 * Generate recommendation text based on fitting results
 * Updated for Phase 3: considers 3P vs 2P preference
 */
function generateRecommendation(results, alpha) {
    const acceptable = results.filter(r => r.status === 'acceptable');
    const unstable = results.filter(r => r.status === 'unstable');

    if (acceptable.length === 0) {
        return {
            type: 'none',
            text: `No candidate passes goodness-of-fit at α=${alpha}; review diagnostics and consider transformations or investigation.`,
            severity: 'error'
        };
    }

    // Identify 2P vs 3P pairs among acceptable fits
    // For each acceptable 3P, check if its 2P base is also acceptable
    const recommended = [];
    const processedBases = new Set();

    for (const result of acceptable) {
        if (result.is3P) {
            // This is a 3P distribution
            const base2P = acceptable.find(r => r.id === result.baseDistribution);
            if (base2P) {
                processedBases.add(base2P.id);
                // Both 2P and 3P are acceptable - recommend based on LRT
                if (result.prefer3P) {
                    recommended.push({
                        ...result,
                        note: 'LRT supports 3P improvement'
                    });
                } else {
                    recommended.push({
                        ...base2P,
                        note: 'Prefer simpler 2P model'
                    });
                }
            } else {
                // Only 3P is acceptable (2P not applicable or rejected)
                recommended.push(result);
            }
        } else if (!processedBases.has(result.id)) {
            // This is a 2P distribution not already processed via its 3P variant
            // Check if there's a 3P variant that's also acceptable
            const variant3P = acceptable.find(r => r.baseDistribution === result.id);
            if (!variant3P) {
                // No 3P variant acceptable, recommend this 2P
                recommended.push(result);
            }
            // If variant3P exists, it will be processed when we encounter it
        }
    }

    if (recommended.length === 0) {
        // Fallback - shouldn't happen but handle gracefully
        return {
            type: 'single',
            text: `Recommended: ${acceptable[0].name} (passes AD at α=${alpha})`,
            distribution: acceptable[0].id,
            severity: 'success'
        };
    }

    if (recommended.length === 1) {
        const rec = recommended[0];
        let noteText = rec.note ? ` (${rec.note})` : '';
        return {
            type: 'single',
            text: `Recommended: ${rec.name}${noteText}`,
            distribution: rec.id,
            severity: 'success'
        };
    }

    // Multiple recommended distributions
    // Check if they're all from different families or competing
    const names = recommended.map(r => r.name).join(', ');
    return {
        type: 'multiple',
        text: `Multiple distributions fit adequately (${names}); prefer simpler models unless AIC/BIC strongly favors another.`,
        distributions: recommended.map(r => r.id),
        severity: 'warning'
    };
}

/**
 * Get distribution by ID
 */
export function getDistribution(id) {
    return DISTRIBUTIONS[id] || null;
}

/**
 * Compute tail probability (out-of-spec probability) using distribution CDF
 * @param {Function} cdf - CDF function for the distribution
 * @param {number|null} lsl - Lower spec limit (or null if none)
 * @param {number|null} usl - Upper spec limit (or null if none)
 * @returns {Object} Tail probability and PPM
 */
export function computeTailProbability(cdf, lsl, usl) {
    if (lsl === null && usl === null) {
        return { probability: null, ppm: null, hasLimits: false };
    }

    let pLower = 0;
    let pUpper = 0;

    if (lsl !== null && isFinite(lsl)) {
        pLower = cdf(lsl);
    }

    if (usl !== null && isFinite(usl)) {
        pUpper = 1 - cdf(usl);
    }

    const probability = pLower + pUpper;
    const ppm = probability * 1e6;

    return {
        probability,
        ppm,
        pLower,
        pUpper,
        hasLimits: true
    };
}

/**
 * Compute distribution moments and summary statistics
 * @param {string} distId - Distribution ID
 * @param {Object} params - Distribution parameters
 * @returns {Object} Moments and derived statistics
 */
export function computeDistributionMoments(distId, params) {
    if (!params) return null;

    switch (distId) {
        case 'normal': {
            const { mu, sigma } = params;
            return {
                mean: mu,
                median: mu,
                mode: mu,
                variance: sigma * sigma,
                stdDev: sigma,
                skewness: 0,
                kurtosis: 0
            };
        }
        case 'lognormal': {
            const { mu, sigma } = params;
            const mean = Math.exp(mu + sigma * sigma / 2);
            const variance = (Math.exp(sigma * sigma) - 1) * Math.exp(2 * mu + sigma * sigma);
            const median = Math.exp(mu);
            const mode = Math.exp(mu - sigma * sigma);
            const skewness = (Math.exp(sigma * sigma) + 2) * Math.sqrt(Math.exp(sigma * sigma) - 1);
            return {
                mean,
                median,
                mode,
                variance,
                stdDev: Math.sqrt(variance),
                skewness,
                muLog: mu,
                sigmaLog: sigma
            };
        }
        case 'lognormal3p': {
            const { threshold, mu, sigma } = params;
            const mean = threshold + Math.exp(mu + sigma * sigma / 2);
            const variance = (Math.exp(sigma * sigma) - 1) * Math.exp(2 * mu + sigma * sigma);
            const median = threshold + Math.exp(mu);
            const mode = threshold + Math.exp(mu - sigma * sigma);
            return {
                mean,
                median,
                mode,
                variance,
                stdDev: Math.sqrt(variance),
                threshold,
                muLog: mu,
                sigmaLog: sigma
            };
        }
        case 'weibull': {
            const { shape, scale } = params;
            const mean = scale * gamma(1 + 1 / shape);
            const variance = scale * scale * (gamma(1 + 2 / shape) - Math.pow(gamma(1 + 1 / shape), 2));
            const median = scale * Math.pow(Math.log(2), 1 / shape);
            const mode = shape > 1 ? scale * Math.pow((shape - 1) / shape, 1 / shape) : 0;
            return {
                mean,
                median,
                mode: shape > 1 ? mode : 0,
                variance,
                stdDev: Math.sqrt(variance),
                shape,
                scale
            };
        }
        case 'weibull3p': {
            const { threshold, shape, scale } = params;
            const mean = threshold + scale * gamma(1 + 1 / shape);
            const variance = scale * scale * (gamma(1 + 2 / shape) - Math.pow(gamma(1 + 1 / shape), 2));
            const median = threshold + scale * Math.pow(Math.log(2), 1 / shape);
            return {
                mean,
                median,
                mode: shape > 1 ? threshold + scale * Math.pow((shape - 1) / shape, 1 / shape) : threshold,
                variance,
                stdDev: Math.sqrt(variance),
                threshold,
                shape,
                scale
            };
        }
        case 'gamma': {
            const { shape, rate } = params;
            const scale = 1 / rate;
            const mean = shape / rate;
            const variance = shape / (rate * rate);
            const median = gammaPPF(0.5, shape, rate);
            const mode = shape >= 1 ? (shape - 1) / rate : 0;
            return {
                mean,
                median,
                mode: shape >= 1 ? mode : 0,
                variance,
                stdDev: Math.sqrt(variance),
                shape,
                rate,
                scale
            };
        }
        case 'gamma3p': {
            const { threshold, shape, rate } = params;
            const mean = threshold + shape / rate;
            const variance = shape / (rate * rate);
            const median = threshold + gammaPPF(0.5, shape, rate);
            return {
                mean,
                median,
                mode: shape >= 1 ? threshold + (shape - 1) / rate : threshold,
                variance,
                stdDev: Math.sqrt(variance),
                threshold,
                shape,
                rate,
                scale: 1 / rate
            };
        }
        case 'exponential': {
            const { rate } = params;
            const mean = 1 / rate;
            const variance = 1 / (rate * rate);
            const median = Math.log(2) / rate;
            return {
                mean,
                median,
                mode: 0,
                variance,
                stdDev: Math.sqrt(variance),
                rate
            };
        }
        case 'johnsonsu': {
            // Johnson SU: z = gamma + delta * asinh((x - xi) / lambda)
            // Mean = xi + lambda * exp(1/(2*delta^2)) * sinh(gamma/delta)
            // Variance is complex, computed from characteristic function
            const { gamma: g, delta: d, xi, lambda: l } = params;
            const expTerm = Math.exp(1 / (2 * d * d));
            const mean = xi + l * expTerm * Math.sinh(g / d);
            // Variance formula for Johnson SU
            const omega = Math.exp(1 / (d * d));
            const variance = (l * l / 2) * (omega - 1) * (omega * Math.cosh(2 * g / d) + 1);
            const median = johnsonSUPPF(0.5, g, d, xi, l);
            return {
                mean,
                median,
                mode: null, // Mode has no simple closed form
                variance,
                stdDev: Math.sqrt(variance),
                gamma: g,
                delta: d,
                xi,
                lambda: l
            };
        }
        case 'johnsonsb': {
            // Johnson SB: bounded distribution
            // Mean and variance are computed using the bounded transformation
            const { gamma: g, delta: d, L, U } = params;
            // For SB, closed-form mean is: L + (U-L) * E[1/(1+exp(-W))] where W ~ N(-g/d, 1/d^2)
            // This integral doesn't have a simple closed form, so we use median instead
            const median = johnsonSBPPF(0.5, g, d, L, U);
            // Approximate mean by numerical integration or use quantile-based estimate
            const q25 = johnsonSBPPF(0.25, g, d, L, U);
            const q75 = johnsonSBPPF(0.75, g, d, L, U);
            const iqr = q75 - q25;
            // Approximate standard deviation from IQR: IQR ≈ 1.35 * sigma for normal
            const approxStdDev = iqr / 1.35;
            return {
                mean: median, // Using median as mean estimate for bounded dist
                median,
                mode: null, // Mode depends on gamma/delta
                variance: approxStdDev * approxStdDev,
                stdDev: approxStdDev,
                gamma: g,
                delta: d,
                L,
                U
            };
        }
        default:
            return null;
    }
}

/**
 * Compute sample shape diagnostics (skewness, kurtosis, robust measures)
 * @param {number[]} data - Array of numeric values
 * @returns {Object} Shape diagnostics
 */
export function computeShapeDiagnostics(data) {
    const n = data.length;
    if (n < 4) {
        return {
            skewness: null,
            excessKurtosis: null,
            bowleySkewness: null,
            robustTailRatio: null,
            nudges: []
        };
    }

    // Compute mean and standard deviation
    const mean = data.reduce((a, b) => a + b, 0) / n;
    const variance = data.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;
    const stdDev = Math.sqrt(variance);

    // Sample skewness (Fisher's definition)
    let m3 = 0;
    for (let i = 0; i < n; i++) {
        m3 += Math.pow((data[i] - mean) / stdDev, 3);
    }
    const skewness = (n / ((n - 1) * (n - 2))) * m3;

    // Sample excess kurtosis
    let m4 = 0;
    for (let i = 0; i < n; i++) {
        m4 += Math.pow((data[i] - mean) / stdDev, 4);
    }
    const excessKurtosis = ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) * m4
                          - (3 * (n - 1) * (n - 1)) / ((n - 2) * (n - 3));

    // Compute quartiles for robust measures
    const sorted = [...data].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(n * 0.25)];
    const q2 = sorted[Math.floor(n * 0.50)]; // median
    const q3 = sorted[Math.floor(n * 0.75)];
    const p5 = sorted[Math.floor(n * 0.05)];
    const p95 = sorted[Math.floor(n * 0.95)];

    // Bowley skewness (quartile skewness)
    const iqr = q3 - q1;
    const bowleySkewness = iqr > 0 ? (q3 + q1 - 2 * q2) / iqr : 0;

    // Robust tail ratio: (P95 - P5) / (P75 - P25)
    const robustTailRatio = iqr > 0 ? (p95 - p5) / iqr : null;

    // Generate interpretation nudges
    const nudges = [];

    if (skewness > 0.5) {
        nudges.push({
            type: 'right_skew',
            text: 'Right-skew suggests lognormal, Weibull, or gamma may be appropriate.',
            severity: 'info'
        });
    } else if (skewness < -0.5) {
        nudges.push({
            type: 'left_skew',
            text: 'Left-skew is unusual; consider data transformations or investigate process.',
            severity: 'info'
        });
    }

    if (excessKurtosis > 2) {
        nudges.push({
            type: 'heavy_tails',
            text: 'Heavy tails may indicate outliers or a heavy-tailed distribution.',
            severity: 'warning'
        });
    } else if (excessKurtosis < -1) {
        nudges.push({
            type: 'light_tails',
            text: 'Light tails suggest data is bounded or truncated.',
            severity: 'info'
        });
    }

    return {
        skewness,
        excessKurtosis,
        bowleySkewness,
        robustTailRatio,
        quartiles: { q1, q2, q3, p5, p95 },
        nudges
    };
}

/**
 * Compute distribution-based capability equivalents using BOTH methods:
 *
 * 1. TAIL-BASED (Minitab-compatible): Maps tail probabilities P(X<LSL) and P(X>USL)
 *    to equivalent Z-scores, then divides by 3 to get capability index.
 *    - Ppk_equiv_tail = min(Z_lower, Z_upper) / 3
 *    - This is the PRIMARY method for Ppk_equiv (matches Minitab)
 *
 * 2. SPREAD-BASED (percentile spread / ISO 22514): Uses PPF(0.00135) and PPF(0.99865)
 *    as natural process spread boundaries (±3σ equivalent for normal).
 *    - Ppk_equiv_spread = min((USL-median)/(xU-median), (median-LSL)/(median-xL))
 *    - Pp_equiv uses this method — the ISO percentile spread (USL-LSL)/(P99.865-P0.135).
 *
 * Pp NOTE: two "Pp" conventions are returned. `Pp_equiv` is the ISO percentile spread
 * above. `Pp_equiv_zbench` is the MINITAB-PARITY Pp = mean of the tail-based PPU/PPL
 * (Z-bench). They agree for a normal fit but diverge for skewed fits (severely for
 * lognormal, whose long upper tail inflates the ISO spread). The UI shows the Minitab
 * value as primary and the ISO value as a labeled secondary.
 *
 * Both methods are computed and returned for all distributions uniformly.
 *
 * @param {Function} ppf - PPF function for the distribution
 * @param {Function} cdf - CDF function for the distribution
 * @param {number[]} data - Array of data values (unused, kept for API compatibility)
 * @param {number|null} lsl - Lower spec limit
 * @param {number|null} usl - Upper spec limit
 * @param {boolean} isUnstable - Whether the fit is unstable
 * @returns {Object} Capability equivalents or unavailable status
 */
export function computeCapabilityEquivalents(ppf, cdf, data, lsl, usl, isUnstable = false) {
    // Guard: if fit is unstable, return unavailable
    if (isUnstable) {
        return {
            available: false,
            reason: 'Fit not stable'
        };
    }

    // Guard: if no PPF function
    if (!ppf) {
        return {
            available: false,
            reason: 'PPF not available'
        };
    }

    // Guard: if no CDF function (required for tail-based)
    if (!cdf) {
        return {
            available: false,
            reason: 'CDF not available'
        };
    }

    // Guard: if no spec limits at all
    if (lsl === null && usl === null) {
        return {
            available: false,
            reason: 'No spec limits defined'
        };
    }

    try {
        // =====================================================================
        // SPREAD-BASED CALCULATIONS (percentile method)
        // =====================================================================

        // Compute natural process spread percentiles (equivalent to ±3σ for normal)
        const xL = ppf(0.00135); // Lower 0.135th percentile
        const xU = ppf(0.99865); // Upper 99.865th percentile

        // Validate percentiles
        if (!isFinite(xL) || !isFinite(xU) || xU <= xL) {
            return {
                available: false,
                reason: 'Invalid percentile computation'
            };
        }

        const spread = xU - xL;

        // Compute median as robust center (P50)
        const median = ppf(0.5);
        if (!isFinite(median)) {
            return {
                available: false,
                reason: 'Invalid median computation'
            };
        }

        // Determine sidedness
        const hasLSL = lsl !== null && isFinite(lsl);
        const hasUSL = usl !== null && isFinite(usl);
        const isTwoSided = hasLSL && hasUSL;
        const isOneSidedLower = hasLSL && !hasUSL;
        const isOneSidedUpper = hasUSL && !hasLSL;

        let sidedness = 'two-sided';
        if (isOneSidedLower) sidedness = 'one-sided (lower)';
        if (isOneSidedUpper) sidedness = 'one-sided (upper)';

        // =====================================================================
        // TAIL-BASED CALCULATIONS (Minitab method)
        // =====================================================================
        //
        // For each spec limit, compute:
        //   pTail = probability in the tail (out of spec)
        //   Z_equiv = -Φ⁻¹(pTail) = equivalent Z-score (positive when in-spec)
        //   Ppk_equiv_tail = Z_equiv / 3
        //
        // This maps the actual OOS probability to what a normal process would need.
        // =====================================================================

        let pBelowLSL = null;
        let pAboveUSL = null;
        let Z_lower = null;
        let Z_upper = null;
        let Ppl_equiv_tail = null;
        let Ppu_equiv_tail = null;
        let Ppk_equiv_tail = null;
        // M3: a side whose OOS probability underflows the 1e-15 clamp (normalCDF returns
        // exactly 1 past ~8.3σ) has its tail Z floored at 7.9413 → the index saturates at a
        // constant 2.6471, masking the true (very high) capability. Track saturation so we
        // can fall back to the finite spread-based index for that side after it's computed.
        let saturatedLower = false;
        let saturatedUpper = false;

        if (hasLSL) {
            pBelowLSL = cdf(lsl);
            // Clamp to avoid -Inf from log(0) - use practical minimum
            const pClamped = Math.max(pBelowLSL, 1e-15);
            saturatedLower = pBelowLSL <= 1e-15;
            // Z_lower = -Φ⁻¹(pBelowLSL) gives positive Z when pBelowLSL is small
            Z_lower = -standardNormalPPF(pClamped);
            if (isFinite(Z_lower)) {
                Ppl_equiv_tail = Z_lower / 3;
            }
        }

        if (hasUSL) {
            pAboveUSL = 1 - cdf(usl);
            // Clamp to avoid -Inf from log(0)
            const pClamped = Math.max(pAboveUSL, 1e-15);
            saturatedUpper = pAboveUSL <= 1e-15;
            // Z_upper = -Φ⁻¹(pAboveUSL) gives positive Z when pAboveUSL is small
            Z_upper = -standardNormalPPF(pClamped);
            if (isFinite(Z_upper)) {
                Ppu_equiv_tail = Z_upper / 3;
            }
        }

        // Ppk_equiv_tail = min of available tail-based indices
        if (Ppl_equiv_tail !== null && Ppu_equiv_tail !== null) {
            Ppk_equiv_tail = Math.min(Ppl_equiv_tail, Ppu_equiv_tail);
        } else if (Ppl_equiv_tail !== null) {
            Ppk_equiv_tail = Ppl_equiv_tail;
        } else if (Ppu_equiv_tail !== null) {
            Ppk_equiv_tail = Ppu_equiv_tail;
        }

        // =====================================================================
        // SPREAD-BASED Ppk CALCULATIONS
        // =====================================================================

        let Pp_equiv = null;
        let Ppl_equiv_spread = null;
        let Ppu_equiv_spread = null;
        let Ppk_equiv_spread = null;

        if (isTwoSided) {
            // Pp only makes sense for two-sided specs
            Pp_equiv = (usl - lsl) / spread;

            // Ppk_spread using median and asymmetric half-spreads
            const upperHalfSpread = xU - median;
            const lowerHalfSpread = median - xL;

            if (upperHalfSpread > 0 && lowerHalfSpread > 0) {
                Ppu_equiv_spread = (usl - median) / upperHalfSpread;
                Ppl_equiv_spread = (median - lsl) / lowerHalfSpread;
                Ppk_equiv_spread = Math.min(Ppu_equiv_spread, Ppl_equiv_spread);
            }
        } else if (isOneSidedLower) {
            const lowerHalfSpread = median - xL;
            if (lowerHalfSpread > 0) {
                Ppl_equiv_spread = (median - lsl) / lowerHalfSpread;
                Ppk_equiv_spread = Ppl_equiv_spread;
            }
        } else if (isOneSidedUpper) {
            const upperHalfSpread = xU - median;
            if (upperHalfSpread > 0) {
                Ppu_equiv_spread = (usl - median) / upperHalfSpread;
                Ppk_equiv_spread = Ppu_equiv_spread;
            }
        }

        // M3: for an ultra-capable side whose tail underflowed the clamp (saturated at
        // 2.6471), fall back to the finite spread-based percentile index — it reflects the
        // true capability instead of the constant ceiling. Only the saturated side changes;
        // the normal capability range (unsaturated) keeps the tail (Minitab-parity) value.
        // This also feeds Pp_equiv_zbench (mean of the two tail sides, computed below).
        if (saturatedUpper && Ppu_equiv_spread !== null && isFinite(Ppu_equiv_spread)) {
            Ppu_equiv_tail = Ppu_equiv_spread;
        }
        if (saturatedLower && Ppl_equiv_spread !== null && isFinite(Ppl_equiv_spread)) {
            Ppl_equiv_tail = Ppl_equiv_spread;
        }
        if (Ppl_equiv_tail !== null && Ppu_equiv_tail !== null) {
            Ppk_equiv_tail = Math.min(Ppl_equiv_tail, Ppu_equiv_tail);
        } else if (Ppl_equiv_tail !== null) {
            Ppk_equiv_tail = Ppl_equiv_tail;
        } else if (Ppu_equiv_tail !== null) {
            Ppk_equiv_tail = Ppu_equiv_tail;
        }

        // =====================================================================
        // RETURN UNIFIED RESULT
        // =====================================================================
        //
        // Primary fields (Ppk_equiv, Ppl_equiv, Ppu_equiv) use TAIL-BASED method
        // for Minitab compatibility. Spread-based values are also provided for
        // reference and diagnostic purposes.
        // =====================================================================

        // =====================================================================
        // MINITAB-PARITY Pp (Z-bench). Minitab's non-normal "Pp" is NOT the ISO
        // percentile spread — it is the mean of the two tail-based one-sided
        // indices: Pp = (PPU_tail + PPL_tail) / 2. Verified exactly on the
        // Minitab benchmark (weibull_like: (0.294384+0.371015)/2 = 0.332699;
        // lognormal: (0.293558+0.363854)/2 = 0.328706). For a normal fit this
        // collapses to classical Pp, same as the ISO value. Two-sided only.
        // See docs/reference/minitab-benchmark-reconciliation.md §3.
        // =====================================================================
        const Pp_equiv_zbench =
            (Ppl_equiv_tail !== null && Ppu_equiv_tail !== null)
                ? (Ppl_equiv_tail + Ppu_equiv_tail) / 2
                : null;

        return {
            available: true,

            // Primary capability equivalents (TAIL-BASED - Minitab compatible)
            Pp_equiv,           // ISO percentile-spread: (USL-LSL)/(P99.865-P0.135)
            Pp_equiv_zbench,    // Minitab-parity Pp: mean of tail PPU/PPL (Z-bench)
            Ppk_equiv: Ppk_equiv_tail,
            Ppl_equiv: Ppl_equiv_tail,
            Ppu_equiv: Ppu_equiv_tail,

            // Explicit tail-based values
            Ppk_equiv_tail,
            Ppl_equiv_tail,
            Ppu_equiv_tail,
            pBelowLSL,
            pAboveUSL,
            Z_lower,
            Z_upper,

            // Explicit spread-based values
            Ppk_equiv_spread,
            Ppl_equiv_spread,
            Ppu_equiv_spread,

            // Process spread metrics (from spread method)
            xL,
            xU,
            spread,
            median,

            // Metadata
            sidedness,
            method: 'tail'  // Primary method is now tail-based
        };
    } catch (e) {
        return {
            available: false,
            reason: 'Computation error: ' + e.message
        };
    }
}

/**
 * Generate conservative recommendation based on fit results
 * Implements the rules: prefer simpler models, use LRT for 3P justification,
 * AIC/BIC only as tie-breaker
 *
 * @param {Array} results - Distribution fit results
 * @param {number} alpha - Significance level
 * @returns {Object} Recommendation with type, text, severity, and selected distribution
 */
export function generateConservativeRecommendation(results, alpha = 0.05) {
    if (!results || results.length === 0) {
        return {
            type: 'none',
            text: 'No distribution fits available.',
            severity: 'error',
            selectedId: null,
            reason: null
        };
    }

    // Filter to acceptable fits (P >= alpha where P exists)
    const acceptable = results.filter(r =>
        r.status === 'acceptable' &&
        r.canFit &&
        !r.isUnstable
    );

    // No acceptable fits - select "least bad" (highest p-value among rejected)
    if (acceptable.length === 0) {
        // Get all fittable distributions sorted by p-value descending
        const fittable = results.filter(r =>
            r.canFit &&
            !r.isUnstable &&
            r.pValue != null
        ).sort((a, b) => (b.pValue ?? 0) - (a.pValue ?? 0));

        const leastBad = fittable[0] || null;

        return {
            type: 'fallback',
            text: leastBad
                ? `No distribution passes GOF (α=${alpha}). Least-rejected: ${leastBad.name} (P=${leastBad.pValue?.toFixed(3)})`
                : `No candidate passes goodness-of-fit at α=${alpha}. Review diagnostics.`,
            severity: 'warning',
            selectedId: leastBad?.id || null,
            reason: 'no_acceptable_fits',
            isFallback: true
        };
    }

    // Single acceptable fit
    if (acceptable.length === 1) {
        const rec = acceptable[0];
        return {
            type: 'single',
            text: `Recommended: ${rec.name}`,
            severity: 'success',
            selectedId: rec.id,
            reason: 'only_acceptable_fit'
        };
    }

    // Multiple acceptable fits - apply conservative rules
    // Rule 1: Group by distribution family and prefer 2P over 3P unless LRT justifies
    const familyMap = new Map();

    for (const fit of acceptable) {
        const family = fit.family || fit.id.replace('3p', '');
        if (!familyMap.has(family)) {
            familyMap.set(family, []);
        }
        familyMap.get(family).push(fit);
    }

    // For each family, select the preferred variant
    const preferredFits = [];

    for (const [, fits] of familyMap) {
        if (fits.length === 1) {
            preferredFits.push(fits[0]);
        } else {
            // Has both 2P and 3P - check LRT
            const fit2P = fits.find(f => !f.is3P);
            const fit3P = fits.find(f => f.is3P);

            if (fit2P && fit3P) {
                // Prefer 3P only if LRT P < alpha AND stable
                if (fit3P.lrtPValue !== null && fit3P.lrtPValue < alpha && !fit3P.isUnstable) {
                    preferredFits.push(fit3P);
                } else {
                    preferredFits.push(fit2P);
                }
            } else {
                // Only one variant available
                preferredFits.push(fits[0]);
            }
        }
    }

    // Now we have one preferred fit per family
    if (preferredFits.length === 1) {
        const rec = preferredFits[0];
        return {
            type: 'single',
            text: `Recommended: ${rec.name}`,
            severity: 'success',
            selectedId: rec.id,
            reason: rec.is3P ? 'lrt_justified_3p' : 'preferred_simpler_model'
        };
    }

    // Multiple families with acceptable fits - use AIC as tie-breaker
    // Sort by AIC (lower is better)
    const sorted = [...preferredFits].sort((a, b) => {
        const aicA = a.aic ?? Infinity;
        const aicB = b.aic ?? Infinity;
        return aicA - aicB;
    });

    const best = sorted[0];
    const second = sorted[1];

    // Check if AIC difference is meaningful (deltaAIC > 2 is typically considered meaningful)
    const aicDiff = (second?.aic ?? Infinity) - (best?.aic ?? 0);

    if (aicDiff > 2) {
        return {
            type: 'single',
            text: `Recommended: ${best.name} (AIC favored)`,
            severity: 'success',
            selectedId: best.id,
            reason: 'aic_tiebreaker'
        };
    }

    // AIC too close - cannot make clear recommendation
    const names = preferredFits.map(f => f.name).slice(0, 3).join(', ');
    return {
        type: 'multiple',
        text: `Multiple distributions fit adequately (${names}). Recommendation is conservative—prefer simpler models.`,
        severity: 'warning',
        selectedId: best.id, // Still provide a default selection
        reason: 'multiple_acceptable',
        alternatives: preferredFits.map(f => f.id)
    };
}

/**
 * Unified decision summary for distribution selection
 * Drives banner, highlighted row, and default sorting so they cannot diverge
 *
 * Policy B Rules:
 * - AD p-values are a gate (acceptable >= alpha vs rejected < alpha)
 * - AIC/BIC are supplemental ranking metrics
 * - LRT is only for 2P vs 3P within-family comparisons (NOT for Johnson SU)
 *
 * @param {Array} results - Distribution fit results
 * @param {Object} diagnostics - Data diagnostics (optional)
 * @param {Object} config - Configuration { alpha: 0.05 }
 * @returns {Object} Decision summary with recommendedKey, sortedResults, bannerInfo, rowStates
 */
export function getDistributionDecisionSummary(results, diagnostics = null, config = { alpha: 0.05, preferNormalWhenAdequate: true }) {
    const { alpha, preferNormalWhenAdequate = true } = config;

    if (!results || results.length === 0) {
        return {
            recommendedKey: null,
            sortedResults: [],
            bannerInfo: {
                type: 'none',
                text: 'No distribution fits available.',
                severity: 'error',
                reason: null
            },
            rowStates: {},
            isFallback: false
        };
    }

    // Compute row states for each distribution
    const rowStates = {};
    for (const r of results) {
        if (r.status === 'awaiting_bounds') {
            rowStates[r.id] = 'awaiting_bounds';
        } else if (!r.canFit) {
            rowStates[r.id] = 'not_applicable';
        } else if (r.isUnstable) {
            rowStates[r.id] = 'unstable';
        } else if (r.status === 'acceptable') {
            rowStates[r.id] = 'acceptable';
        } else {
            rowStates[r.id] = 'rejected';
        }
    }

    // Check for Johnson SB awaiting bounds (for banner suggestion)
    const sbAwaitingBounds = results.find(r =>
        r.id === 'johnsonsb' &&
        r.eligibilityStatus === ELIGIBILITY_STATUS.ELIGIBLE_WITH_CONFIRMATION
    );

    // Filter to acceptable fits that are ELIGIBLE for recommendation
    // Distributions with ELIGIBLE_WITH_CONFIRMATION or INELIGIBLE are excluded
    const acceptable = results.filter(r =>
        r.status === 'acceptable' &&
        r.canFit &&
        !r.isUnstable &&
        r.eligibilityStatus === ELIGIBILITY_STATUS.ELIGIBLE
    );

    // Group by family for LRT comparisons
    const familyMap = new Map();
    for (const fit of acceptable) {
        // Use explicit family if defined, otherwise derive from ID
        // Johnson SU has family: 'johnsonsu', so no LRT comparison
        const family = fit.family || fit.id.replace('3p', '');
        if (!familyMap.has(family)) {
            familyMap.set(family, []);
        }
        familyMap.get(family).push(fit);
    }

    // Apply LRT within families to select preferred variant
    const preferredFits = [];
    for (const [, fits] of familyMap) {
        if (fits.length === 1) {
            preferredFits.push(fits[0]);
        } else {
            // Multiple fits in family - check for 2P vs 3P
            const fit2P = fits.find(f => !f.is3P);
            const fit3P = fits.find(f => f.is3P);

            if (fit2P && fit3P) {
                // LRT decision: prefer 3P only if LRT P < alpha AND stable
                if (fit3P.lrtPValue !== null && fit3P.lrtPValue < alpha && !fit3P.isUnstable) {
                    preferredFits.push(fit3P);
                    rowStates[fit3P.id] = 'preferred_3p';
                } else {
                    preferredFits.push(fit2P);
                    rowStates[fit2P.id] = 'preferred_2p';
                }
            } else {
                // Only one variant available
                preferredFits.push(fits[0]);
            }
        }
    }

    // Sort preferred fits by AIC for ranking
    const sortedPreferred = [...preferredFits].sort((a, b) => {
        const aicA = a.aic ?? Infinity;
        const aicB = b.aic ?? Infinity;
        return aicA - aicB;
    });

    // Determine recommended distribution
    let recommendedKey = null;
    let bannerInfo;
    let isFallback = false;
    let normalPreferred = false;

    // Check if Normal is eligible for preference (Policy: prefer Normal when adequate)
    // This runs AFTER eligibility filtering and LRT selection, BEFORE AIC tie-breaking
    const normalFit = preferredFits.find(f => f.id === 'normal');
    const normalIsEligible = normalFit &&
        normalFit.canFit === true &&
        normalFit.status === 'acceptable' &&
        !normalFit.isUnstable &&
        typeof normalFit.pValue === 'number' &&
        normalFit.pValue >= alpha;

    if (acceptable.length === 0) {
        // No acceptable fits - fallback to least bad (only from ELIGIBLE distributions)
        const fittable = results.filter(r =>
            r.canFit &&
            !r.isUnstable &&
            r.pValue != null &&
            r.eligibilityStatus === ELIGIBILITY_STATUS.ELIGIBLE
        ).sort((a, b) => (b.pValue ?? 0) - (a.pValue ?? 0));

        const leastBad = fittable[0] || null;
        recommendedKey = leastBad?.id || null;
        isFallback = true;

        if (recommendedKey) {
            rowStates[recommendedKey] = 'fallback_recommended';
        }

        // Build banner text with optional SB suggestion
        let bannerText = leastBad
            ? `No distribution passes GOF (α=${alpha}). Least-rejected: ${leastBad.name} (P=${leastBad.pValue?.toFixed(3)})`
            : `No candidate passes goodness-of-fit at α=${alpha}. Review diagnostics.`;

        // If SB is awaiting bounds and might help, suggest enabling bounds
        if (sbAwaitingBounds) {
            bannerText += ' Consider enabling Johnson SB bounds if data has known limits.';
        }

        bannerInfo = {
            type: 'fallback',
            text: bannerText,
            severity: 'warning',
            reason: 'no_acceptable_fits',
            sbAwaitingBounds: !!sbAwaitingBounds
        };
    } else if (sortedPreferred.length === 1) {
        // Single preferred fit
        recommendedKey = sortedPreferred[0].id;
        rowStates[recommendedKey] = 'recommended';

        bannerInfo = {
            type: 'single',
            text: `Recommended: ${sortedPreferred[0].name}`,
            severity: 'success',
            reason: sortedPreferred[0].is3P ? 'lrt_justified_3p' : 'preferred_simpler_model'
        };
    } else {
        // Multiple preferred fits
        const best = sortedPreferred[0];
        const second = sortedPreferred[1];
        const aicDiff = (second?.aic ?? Infinity) - (best?.aic ?? 0);

        // Policy: Prefer Normal when adequate (conservative SPC convention)
        // If Normal is acceptable and config allows, recommend Normal regardless of AIC ranking
        if (preferNormalWhenAdequate && normalIsEligible) {
            recommendedKey = 'normal';
            normalPreferred = true;
            rowStates[recommendedKey] = 'recommended';

            const names = sortedPreferred.map(f => f.name).slice(0, 3).join(', ');
            bannerInfo = {
                type: 'multiple',
                text: `Multiple distributions fit adequately (${names}); defaulting to Normal per conservative SPC convention.`,
                severity: 'success',
                reason: 'normal_preferred_spc_convention',
                alternatives: sortedPreferred.map(f => f.id)
            };
        } else {
            // Standard AIC tie-breaking when Normal not preferred or not eligible
            recommendedKey = best.id;
            rowStates[recommendedKey] = 'recommended';

            if (aicDiff > 2) {
                bannerInfo = {
                    type: 'single',
                    text: `Recommended: ${best.name} (AIC favored)`,
                    severity: 'success',
                    reason: 'aic_tiebreaker'
                };
            } else {
                const names = sortedPreferred.map(f => f.name).slice(0, 3).join(', ');
                bannerInfo = {
                    type: 'multiple',
                    text: `Multiple distributions fit adequately (${names}). Recommendation is conservative.`,
                    severity: 'warning',
                    reason: 'multiple_acceptable',
                    alternatives: sortedPreferred.map(f => f.id)
                };
            }
        }
    }

    // Sort all results according to Policy B:
    // 1. Recommended first
    // 2. Then acceptable (by descending p-value, then ascending deltaAIC)
    // 3. Then rejected (by descending p-value)
    // 4. Then awaiting_bounds (conditional distributions)
    // 5. Then unstable
    // 6. Then not applicable
    const sortedResults = [...results].sort((a, b) => {
        // Recommended always first
        if (a.id === recommendedKey) return -1;
        if (b.id === recommendedKey) return 1;

        // Status priority
        const statusOrder = {
            acceptable: 0,
            rejected: 1,
            awaiting_bounds: 2,
            unstable: 3,
            not_applicable: 4,
            fit_failed: 5,
            unknown: 6
        };
        const statusDiff = (statusOrder[a.status] ?? 6) - (statusOrder[b.status] ?? 6);
        if (statusDiff !== 0) return statusDiff;

        // Within same status, sort by p-value descending, then deltaAIC ascending
        if (a.pValue !== b.pValue) {
            return (b.pValue ?? 0) - (a.pValue ?? 0);
        }
        return (a.deltaAIC ?? 0) - (b.deltaAIC ?? 0);
    });

    return {
        recommendedKey,
        sortedResults,
        bannerInfo,
        rowStates,
        isFallback,
        normalPreferred,
        preferredByFamily: sortedPreferred.map(f => f.id),
        diagnosticsWarnings: diagnostics ? Object.values(diagnostics).filter(d => d.severity === 'warning') : []
    };
}

// Default export (back-compat for `import DistributionEngine from ...`).
const DistributionEngine = {
    fitAllDistributions,
    getDistribution,
    DISTRIBUTIONS,
    ELIGIBILITY_STATUS,
    computeTailProbability,
    computeDistributionMoments,
    computeShapeDiagnostics,
    computeCapabilityEquivalents,
    generateConservativeRecommendation,
    getDistributionDecisionSummary,
    normalCDF,
};
export default DistributionEngine;
